import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Cell,
  CartesianGrid,
  Legend,
} from "recharts";
import * as XLSX from "xlsx";
import Menu, { MenuItem } from "../Menu";
import Modal from "../Modal";
import Tooltip from "../Tooltip";
import VideoModal from "../VideoModal";
import { useUser } from "../../contexts/UserContext";
import { useMultiLot } from "../../contexts/MultiLotContext";
import {
  aiChatService,
  type AiAgentNameItem,
  type AiConversationSummary,
  type AiMessageItem,
  type AiRateInfo,
} from "../../utils/api";
import "./Chat.css";
import {
  CHAT_MESSAGE_SENT_EVENT,
  CHAT_WALKTHROUGH_SET_INPUT_EVENT,
  CHAT_WALKTHROUGH_SET_MODE_EVENT,
  CHAT_WALKTHROUGH_SET_WIDTH_EVENT,
  CHAT_WALKTHROUGH_SET_TAGGED_AGENTS_EVENT,
  CHAT_WALKTHROUGH_CLEAR_TAGGED_AGENTS_EVENT,
  WALKTHROUGH_STATE_EVENT,
} from "../Walkthrough/walkthroughState";

type ChatRole = "user" | "assistant";
type ChatMode = "ask" | "agent";
type ChatVisibility = "closed" | "open";
export const AGENT_ACTION_APPLIED_EVENT = "agent-action-applied";
// Broadcast whenever the chat's effective open/closed state changes so other surfaces (e.g. the
// dashboard tutorial button) can get out of the way while the chat is open.
export const AI_CHAT_VISIBILITY_EVENT = "ai-chat-visibility-change";

type ChatProps = {
  isMobile?: boolean;
  isMobileOpen?: boolean;
  onMobileVisibilityChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  maxWidth?: number;
};


type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
  createdOn?: string | null;
  pending?: boolean;
  animate?: boolean;
  rawJson?: any;
  attachmentItems?: { name: string; kind: "image" | "sheet" }[];
  // Sent to the backend but not shown as a user bubble (e.g. a clarifying-question answer picked via
  // the UI — the highlighted option already shows what they chose).
  hiddenFromChat?: boolean;
};

const ALL_SUGGESTIONS = [
  "Why is occupancy lower today?",
  "How do I boost tomorrow's occupancy?",
  "Summarize today's occupancy.",
  "How do I remove a violation?",
  "How do I add someone to registry?",
  "How do I change my password?",
  "Can I mark a notification unread?",
  "Can I set pricing per day?",
  "How do I enable permit-only?",
  "Can I temporarily set free parking?",
  "What's my lot address?",
  "How many occupants this week?",
  "What day had the highest occupancy this week?",
];

const AGENT_MODE_SUGGESTIONS = [
  "Set my hourly rate to 2 dollars, and daily max to 10.",
  "I want to update the registry.",
  "Here's a list of plates to add to the registry",
  "Increase lot capacity by 5.",
  "How many violations where issued today?",
  "How full is my lot right now?",
  "Please diagnose why occupancy is low today.",
  "Summarize trends in occupancy this week.",
  "What's my highest revenue day this month?",
];

const CLOSED_WIDTH = 54;
const DEFAULT_OPEN_WIDTH = 300;
const MIN_WIDTH = 230;
const MAX_WIDTH = 600;
const LARGE_THRESHOLD = 400;
const SNAP_CLOSE_THRESHOLD = MIN_WIDTH - 130;
const WIDTH_STORAGE_KEY = "ai_chat_width";
// Exported so other surfaces can read the persisted desktop open/closed state on mount.
export const VISIBILITY_STORAGE_KEY = "ai_chat_visibility";
const MODE_STORAGE_KEY = "ai_chat_mode_preferences";
const ACTIVE_CONVERSATION_STORAGE_KEY = "ai_chat_active_conversations";
const AGENT_CONFIRM_STORAGE_KEY = "ai_chat_last_agent_confirm";
const AGENT_CONFIRM_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const DATA_CONSENT_STORAGE_KEY = "ai_chat_last_data_consent";
const DATA_CONSENT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

// Whether the operator has consented (within the last 24h) to let the AI read live lot data.
const hasRecentDataConsent = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const raw = window.localStorage.getItem(DATA_CONSENT_STORAGE_KEY);
  const ts = raw ? Number(raw) : NaN;
  return Number.isFinite(ts) && Date.now() - ts < DATA_CONSENT_COOLDOWN_MS;
};

const rememberDataConsent = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.setItem(DATA_CONSENT_STORAGE_KEY, String(Date.now()));
};

// Route-based auto-tagging: maps route patterns to agent keys that should be auto-tagged
const ROUTE_AUTO_TAG_AGENTS: Record<string, string[]> = {
  "/dashboard": ["data_analysis_agent"],
  "/occupants": ["occupants_agent"],
  "/advanced": ["advanced_pricing_agent"],
  "/settings": ["lot_pricing_agent", "lot_settings_agent"],
  "/registry": ["lot_registry_agent"],
  "/violators": ["violators_agent"],
};

const HISTORY_LIMIT = 20;
const MESSAGE_PAGE_LIMIT = 100;
const MAX_FETCH_PAGES = 50;
const THINKING_WORDS = [
  "Working",
  "Thinking",
  "Querying",
  "Planning",
  "Verifying",
  "Analyzing",
  "Researching",
  "Drafting",
  "Optimizing",
  "Testing",
  "Refining",
  "Reviewing",
  "Preparing",
  "Calculating"
];
const CHAT_THINKING_WORDS = [
  "Working",
  "Thinking",
  "Analyzing",
  "Drafting",
  "Researching",
  "Pondering",
];
const THINKING_DOTS = [".", "..", "..."];

const pickRandomThinkingWord = (words: string[], previous?: string): string => {
  if (words.length === 0) {
    return "Working";
  }
  const pool = words.filter((word) => word !== previous);
  const options = pool.length > 0 ? pool : words;
  const index = Math.floor(Math.random() * options.length);
  return options[index] ?? words[0];
};

const isChatMode = (value: unknown): value is ChatMode => value === "ask" || value === "agent";

const readStoredModes = (): Record<string, ChatMode> => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    return Object.entries(parsed).reduce<Record<string, ChatMode>>((acc, [key, value]) => {
      if (typeof key === "string" && isChatMode(value)) {
        acc[key] = value;
      }
      return acc;
    }, {});
  } catch (error) {
    console.error("Failed to read stored chat modes", error);
    return {};
  }
};

const writeStoredModes = (value: Record<string, ChatMode>) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(MODE_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error("Failed to write stored chat modes", error);
  }
};

type StoredConversationMap = Record<string, Record<string, string>>;

const getChatSessionKey = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  const refreshExpiration = window.localStorage.getItem("refreshTokenExpiresAt");
  if (refreshExpiration) {
    return `session:${refreshExpiration}`;
  }
  const loggedInUserId = window.localStorage.getItem("loggedInUserId")
    || window.localStorage.getItem("userId");
  if (loggedInUserId) {
    return `user:${loggedInUserId}`;
  }
  return null;
};

const readStoredActiveConversations = (): StoredConversationMap => {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(ACTIVE_CONVERSATION_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return {};
    }
    return parsed as StoredConversationMap;
  } catch (error) {
    console.error("Failed to read stored chat conversations", error);
    return {};
  }
};

const writeStoredActiveConversations = (value: StoredConversationMap) => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(ACTIVE_CONVERSATION_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error("Failed to persist chat conversations", error);
  }
};

const readStoredConversationId = (sessionKey: string, lotId: string): string | null => {
  if (!sessionKey || !lotId) {
    return null;
  }
  const stored = readStoredActiveConversations();
  return stored?.[sessionKey]?.[lotId] ?? null;
};

const persistStoredConversationId = (sessionKey: string, lotId: string, conversationId: string | null) => {
  if (!sessionKey || !lotId || typeof window === "undefined") {
    return;
  }
  const stored = readStoredActiveConversations();
  const currentSessionMap = { ...(stored[sessionKey] || {}) };

  if (conversationId) {
    if (currentSessionMap[lotId] === conversationId) {
      return;
    }
    stored[sessionKey] = {
      ...currentSessionMap,
      [lotId]: conversationId,
    };
  } else if (currentSessionMap[lotId]) {
    delete currentSessionMap[lotId];
    if (Object.keys(currentSessionMap).length === 0) {
      delete stored[sessionKey];
    } else {
      stored[sessionKey] = currentSessionMap;
    }
  } else {
    return;
  }
  writeStoredActiveConversations(stored);
};

type ChartDataPoint = {
  label: string;
  value: number;
  color?: string;
};

type ChartDataset = {
  label: string;
  data: ChartDataPoint[];
  color?: string;
  yAxis?: "left" | "right";
};

type ChartOptions = {
  title?: string;
  xLabel?: string;
  yLabel?: string;
  colors?: string[];
  height?: number;
  showLegend?: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  yAxisLeftLabel?: string;
  yAxisRightLabel?: string;
};

type MarkdownBlock =
  | { type: "paragraph"; content: string }
  | { type: "heading"; level: number; content: string }
  | { type: "unordered-list"; items: string[] }
  | { type: "ordered-list"; items: string[] }
  | { type: "divider" }
  | {
    type: "table";
    header: string[];
    rows: string[][];
    alignments: Array<"left" | "center" | "right" | null>;
  }
  | { type: "youtube"; url: string; videoId: string }
  | {
    type: "chart";
    chartType: "line" | "bar" | "pie";
    data?: ChartDataPoint[];
    datasets?: ChartDataset[];
    options?: ChartOptions;
  };

type InlineToken = "button" | "link" | "bold" | "italic" | "auto-link" | "email";
type InlineMatch = { type: InlineToken; match: RegExpExecArray; index: number };

const INLINE_PATTERNS: { type: InlineToken; regex: RegExp }[] = [
  { type: "button", regex: /\[button:([^\]]+)\]\(([^)]+)\)/i },
  { type: "link", regex: /\[([^\]]+)\]\(([^)]+)\)/ },
  { type: "bold", regex: /\*\*([^*]+)\*\*/ },
  { type: "bold", regex: /__([^_]+)__/ },
  { type: "italic", regex: /\*([^*]+)\*/ },
  { type: "italic", regex: /_([^_]+)_/ },
  { type: "auto-link", regex: /(https?:\/\/[^\s)]+)/i },
  { type: "email", regex: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/i },
];

const YOUTUBE_PATTERN = "(?:https?:\\/\\/)?(?:www\\.)?(?:youtube\\.com\\/watch\\?v=|youtu\\.be\\/)([A-Za-z0-9_-]{11})(?:[?&][^\\s)]*)?";
const YOUTUBE_REGEX = new RegExp(YOUTUBE_PATTERN, "i");
const TABLE_DIVIDER_CELL_REGEX = /^:?-{3,}:?$/;

const extractMarkdown = (content: string, contentJson?: any) => {
  const markdown = contentJson?.assistant_message?.markdown;
  if (typeof markdown === "string" && markdown.trim().length > 0) {
    return markdown.trim();
  }
  return content?.trim() ?? "";
};

const normalizeHref = (href?: string) => {
  if (!href) {
    return "#";
  }
  const trimmed = href.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed) || trimmed.startsWith("/")) {
    return trimmed;
  }
  return `https://${trimmed.replace(/^:+/, "")}`;
};

const extractYoutubeDetails = (value: string): { url: string; videoId: string; matchedText: string } | null => {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(YOUTUBE_REGEX);
  if (!match || !match[1]) return null;
  const videoId = match[1];
  const url = `https://www.youtube.com/watch?v=${videoId}`;
  return { url, videoId, matchedText: match[0] };
};

const getYoutubeThumbnail = (videoId: string) => `https://img.youtube.com/vi/${videoId}/0.jpg`;

const findYoutubeLinks = (text: string): Array<{ videoId: string; url: string }> => {
  if (!text) {
    return [];
  }
  const regex = new RegExp(YOUTUBE_PATTERN, "gi");
  const matches: Array<{ videoId: string; url: string }> = [];
  const seen = new Set<string>();
  let result: RegExpExecArray | null;
  while ((result = regex.exec(text)) !== null) {
    const videoId = result[1];
    if (!videoId || seen.has(videoId)) {
      continue;
    }
    seen.add(videoId);
    matches.push({ videoId, url: `https://www.youtube.com/watch?v=${videoId}` });
  }
  return matches;
};

const splitTableRow = (value: string): string[] | null => {
  if (!value || !value.includes("|")) {
    return null;
  }
  const normalized = value.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells = normalized.split("|").map((cell) => cell.trim());
  return cells.length ? cells : null;
};

const isTableDividerRow = (cells: string[] | null) => {
  if (!cells || !cells.length) {
    return false;
  }
  return cells.every((cell) => TABLE_DIVIDER_CELL_REGEX.test(cell.replace(/\s+/g, "")));
};

const parseAlignmentFromDivider = (cell: string): "left" | "center" | "right" | null => {
  const trimmed = cell.trim();
  const hasLeft = trimmed.startsWith(":");
  const hasRight = trimmed.endsWith(":");
  if (hasLeft && hasRight) {
    return "center";
  }
  if (hasRight) {
    return "right";
  }
  if (hasLeft) {
    return "left";
  }
  return null;
};

const renderYoutubePreview = (
  videoId: string,
  url: string,
  key: string,
  onYoutubeOpen: (target: string) => void,
) => (
  <button
    type="button"
    key={key}
    className="ai-chat-youtube-preview"
    onClick={() => onYoutubeOpen(url)}
    aria-label="Open YouTube video"
  >
    <span className="ai-chat-youtube-thumbnail">
      <img src={getYoutubeThumbnail(videoId)} alt="YouTube preview" />
      <span className="ai-chat-youtube-play" aria-hidden="true" />
    </span>
  </button>
);

// Build the single message sent to the AI once every clarifying question is answered. A lone question
// sends just the chosen option (unchanged behavior); multiple questions send a labeled summary.
const buildClarifyAnswer = (questions: any[], selections: Record<number, string>): string => {
  if (questions.length === 1) {
    return selections[0] ?? "";
  }
  return questions
    .map((q, i) => {
      const qText = typeof q?.question === "string" && q.question.trim()
        ? q.question.trim()
        : `Question ${i + 1}`;
      return `${qText}\n- ${selections[i] ?? ""}`;
    })
    .join("\n\n");
};

// ISO currency codes the model tends to emit, mapped to the symbol we'd rather show.
const CURRENCY_CODE_SYMBOL: Record<string, string> = {
  USD: "$", CAD: "$", AUD: "$", NZD: "$", HKD: "$", SGD: "$", MXN: "$",
  EUR: "€", GBP: "£", JPY: "¥", CNY: "¥", INR: "₹",
};

// Tidy money in any string: "CAD 5" / "USD 5.00" / "CA$5" → "$5"; "5 dollars" → "$5"; drop ".00".
const prettifyMoney = (input: string): string => {
  if (!input) {
    return input;
  }
  return input
    // ISO code + amount → symbol + amount (only known currency codes are touched).
    .replace(/\b([A-Z]{3})\s?\$?\s?(\d[\d,]*(?:\.\d+)?)/g, (whole, code, num) => {
      const symbol = CURRENCY_CODE_SYMBOL[code as string];
      return symbol ? `${symbol}${num}` : whole;
    })
    // Locale dollar prefixes like "CA$5" / "US$5" → "$5".
    .replace(/\b(?:CA|US|AU|NZ|HK|SG|MX)\$/g, "$")
    // "5 dollars" → "$5".
    .replace(/(\d[\d,]*(?:\.\d+)?)\s*dollars?\b/gi, "$$$1")
    // Drop trailing .00 on whole amounts: "$5.00" → "$5".
    .replace(/([$€£¥₹])(\d[\d,]*)\.0+\b/g, "$1$2");
};

const formatAgentValue = (value: any, currency?: string) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Enabled" : "Disabled";
  }
  if (typeof value === "number") {
    if (currency) {
      try {
        return prettifyMoney(new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value));
      } catch {
        return value.toFixed(2);
      }
    }
    return value.toLocaleString();
  }
  return prettifyMoney(String(value));
};

// Plate rendering: reuse the app's plate look (compact variant) inside chat tables and agent previews.
// Applied column/row-driven (a "Plate" header or a "Plate" preview row) so we never badge prose.
const PLATE_CELL_REGEX = /^[A-Za-z0-9][A-Za-z0-9-]{1,9}$/;
const looksLikePlate = (value: unknown): boolean => {
  const v = typeof value === "string" ? value.trim() : "";
  return v.length >= 2 && v.length <= 10 && PLATE_CELL_REGEX.test(v);
};
const isPlateHeader = (header: unknown): boolean => {
  const h = (typeof header === "string" ? header : "").trim().toLowerCase();
  return ["plate", "plates", "plate #", "plate no", "plate number", "license plate"].includes(h);
};
const renderPlateBadge = (value: string, key?: string) => (
  <span key={key} className="ai-chat-plate">{value.trim()}</span>
);

// Status chips: a cell whose whole value is a recognized status word renders as a colored chip
// (reusing the agent-status badge styles). Matched on the exact value so prose is never chipped.
// Class suffix maps to a color: "" = yellow (pending), is-applied = green, is-expired = red,
// is-cancelled = grey.
const STATUS_CHIP_CLASS: Record<string, string> = {
  // green — good / settled / active
  active: "is-applied", paid: "is-applied", resolved: "is-applied", approved: "is-applied",
  valid: "is-applied", completed: "is-applied", settled: "is-applied", enabled: "is-applied",
  // red — bad / overdue / expired
  expired: "is-expired", unpaid: "is-expired", overdue: "is-expired", outstanding: "is-expired",
  overstay: "is-expired", overstaying: "is-expired", denied: "is-expired", rejected: "is-expired",
  failed: "is-expired", open: "is-expired", disabled: "is-expired",
  // yellow (base) — pending / in progress
  pending: "", disputed: "", appealed: "", processing: "", issued: "", review: "",
  scheduled: "", warning: "", flagged: "",
  // grey — neutral / done / inactive
  cancelled: "is-cancelled", canceled: "is-cancelled", dismissed: "is-cancelled",
  void: "is-cancelled", voided: "is-cancelled", inactive: "is-cancelled",
  archived: "is-cancelled", removed: "is-cancelled", departed: "is-cancelled", closed: "is-cancelled",
};
const getStatusChip = (value: unknown): { label: string; cls: string } | null => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed || trimmed.length > 18) {
    return null;
  }
  const key = trimmed.toLowerCase().replace(/[^a-z]/g, "");
  if (!(key in STATUS_CHIP_CLASS)) {
    return null;
  }
  return { label: trimmed, cls: STATUS_CHIP_CLASS[key] };
};
const renderStatusChip = (value: string, key?: string) => {
  const chip = getStatusChip(value);
  if (!chip) {
    return null;
  }
  return (
    <span key={key} className={`ai-chat-agent-status ${chip.cls}`.trim()}>{chip.label}</span>
  );
};

const renderAgentPreviewContent = (preview: any): React.ReactNode => {
  if (!preview || typeof preview !== "object") {
    return null;
  }
  const type = (preview.type ?? "").toLowerCase();
  if (type === "table") {
    const columns = Array.isArray(preview.columns) && preview.columns.length > 0
      ? preview.columns
      : [
        { id: "label", label: "Setting" },
        { id: "current", label: "Current" },
        { id: "proposed", label: "New" },
      ];
    const rows = Array.isArray(preview.rows) ? preview.rows : [];
    return (
      <table className="ai-chat-agent-table">
        <thead>
          <tr>
            {columns.map((column: any) => (
              <th key={column.id ?? column.label}>{column.label ?? column.id ?? ""}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="ai-chat-agent-empty">
                No fields provided.
              </td>
            </tr>
          ) : (
            rows.map((row: any, index: number) => (
              <tr
                key={row.id ?? `${row.label ?? "row"}-${index}`}
                className={row.changed ? "ai-chat-agent-row is-changed" : "ai-chat-agent-row"}
              >
                {columns.map((column: any) => {
                  const colId = column.id ?? column.label;
                  const value = row[colId];
                  const rowIsPlate = typeof row.label === "string"
                    && row.label.trim().toLowerCase().includes("plate");
                  if (colId !== "label" && rowIsPlate && looksLikePlate(value)) {
                    return <td key={colId}>{renderPlateBadge(String(value))}</td>;
                  }
                  return (
                    <td key={colId}>{formatAgentValue(value, preview.currency)}</td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  }
  if (type === "list") {
    const items = Array.isArray(preview.items) ? preview.items : [];
    return (
      <ul className="ai-chat-agent-list">
        {items.map((item: any, index: number) => (
          <li key={item.id ?? index}>{item.label ?? item.text ?? JSON.stringify(item)}</li>
        ))}
      </ul>
    );
  }
  if (preview.markdown) {
    return (
      <div className="ai-chat-agent-summary">
        {String(preview.markdown).split("\n").map((line: string, index: number) => (
          <p key={`preview-line-${index}`}>{line}</p>
        ))}
      </div>
    );
  }
  return (
    <pre className="ai-chat-agent-summary">
      {JSON.stringify(preview, null, 2)}
    </pre>
  );
};

const parseMarkdownBlocks = (raw: string): MarkdownBlock[] => {
  const content = raw?.trim();
  if (!content) {
    return [];
  }

  const lines = content.split(/\r?\n/);
  const blocks: MarkdownBlock[] = [];
  let index = 0;

  const headingMatch = (value: string) => value.trimStart().match(/^(#{1,4})\s+(.*)$/);
  const unorderedMatch = (value: string) => value.trimStart().match(/^[-*•]\s+(.*)$/);
  const orderedMatch = (value: string) => value.trimStart().match(/^\d+\.\s+(.*)$/);
  const dividerMatch = (value: string) => value.trim().match(/^(-{3,}|_{3,}|\*{3,})$/);

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const potentialHeader = splitTableRow(line);
    if (potentialHeader && index + 1 < lines.length) {
      const dividerLine = lines[index + 1];
      const dividerCells = splitTableRow(dividerLine);
      if (
        dividerCells
        && potentialHeader.length === dividerCells.length
        && isTableDividerRow(dividerCells)
      ) {
        const alignments = dividerCells.map((cell) => parseAlignmentFromDivider(cell));
        const rows: string[][] = [];
        let rowIndex = index + 2;
        while (rowIndex < lines.length) {
          const rowLine = lines[rowIndex];
          if (!rowLine.trim()) {
            break;
          }
          const rowCells = splitTableRow(rowLine);
          if (!rowCells) {
            break;
          }
          rows.push(rowCells);
          rowIndex += 1;
        }
        blocks.push({
          type: "table",
          header: potentialHeader,
          rows,
          alignments,
        });
        index = rowIndex;
        continue;
      }
    }

    if (dividerMatch(line)) {
      blocks.push({ type: "divider" });
      index += 1;
      continue;
    }

    // Check for chart code blocks. Tolerant of 3+ backticks (models sometimes emit 4-6) and JSON that
    // starts on the same line as the opening fence.
    const chartBlockMatch = line.trim().match(/^`{3,}chart:(line|bar|pie)\b\s*(.*)$/i);
    if (chartBlockMatch) {
      const chartType = chartBlockMatch[1].toLowerCase() as "line" | "bar" | "pie";
      const jsonLines: string[] = [];
      const inlineAfterFence = chartBlockMatch[2] ?? "";
      if (inlineAfterFence.trim()) {
        jsonLines.push(inlineAfterFence);
      }
      index += 1;

      while (index < lines.length && !/^`{3,}/.test(lines[index].trim())) {
        jsonLines.push(lines[index]);
        index += 1;
      }

      // Skip closing fence (3+ backticks)
      if (index < lines.length && /^`{3,}/.test(lines[index].trim())) {
        index += 1;
      }

      try {
        // Strip any trailing fence that rode inline with the JSON.
        const jsonStr = jsonLines.join("\n").trim().replace(/`{3,}\s*$/, "").trim();
        const chartJson = JSON.parse(jsonStr);
        const normalizePoint = (point: any): ChartDataPoint | null => {
          if (!point || typeof point !== "object") {
            return null;
          }
          const rawLabel = typeof point.label === "string" || typeof point.label === "number"
            ? point.label
            : typeof point.x === "string" || typeof point.x === "number"
              ? point.x
              : "";
          const label = String(rawLabel || "").trim();
          const rawValue = point.value ?? point.y;
          const value = Number(rawValue);
          if (!label || Number.isNaN(value)) {
            return null;
          }
          return {
            label,
            value,
            color: typeof point.color === "string" ? point.color : undefined,
          };
        };

        const chartData: ChartDataPoint[] = Array.isArray(chartJson.data)
          ? chartJson.data
            .map((d: unknown): ChartDataPoint | null => normalizePoint(d))
            .filter((d: ChartDataPoint | null): d is ChartDataPoint => Boolean(d))
          : [];

        const datasets: ChartDataset[] = Array.isArray(chartJson.datasets)
          ? chartJson.datasets
            .map((dataset: any, index: number) => {
              const normalizedData = Array.isArray(dataset?.data)
                ? dataset.data
                  .map((point: unknown): ChartDataPoint | null => normalizePoint(point))
                  .filter((point: ChartDataPoint | null): point is ChartDataPoint => Boolean(point))
                : [];
              if (!normalizedData.length) {
                return null;
              }
              const datasetLabel = typeof dataset?.label === "string" && dataset.label.trim().length
                ? dataset.label
                : `Series ${index + 1}`;
              const yAxis = dataset?.yAxis === "right"
                ? "right"
                : dataset?.yAxis === "left"
                  ? "left"
                  : undefined;

              return {
                label: datasetLabel,
                data: normalizedData,
                color: typeof dataset?.color === "string" ? dataset.color : undefined,
                yAxis,
              };
            })
            .filter((dataset: ChartDataset | null): dataset is ChartDataset => Boolean(dataset))
          : [];

        if (chartData.length > 0 || datasets.length > 0) {
          blocks.push({
            type: "chart",
            chartType,
            data: chartData.length ? chartData : undefined,
            datasets: datasets.length ? datasets : undefined,
            options: {
              title: chartJson.title,
              xLabel: chartJson.options?.xLabel ?? chartJson.xLabel,
              yLabel: chartJson.options?.yLabel ?? chartJson.yLabel,
              colors: chartJson.options?.colors,
              height: chartJson.options?.height,
              showLegend: chartJson.showLegend ?? chartJson.options?.showLegend,
              valuePrefix: chartJson.options?.valuePrefix,
              valueSuffix: chartJson.options?.valueSuffix,
              yAxisLeftLabel: chartJson.yAxisLeft?.label ?? chartJson.options?.yAxisLeftLabel,
              yAxisRightLabel: chartJson.yAxisRight?.label ?? chartJson.options?.yAxisRightLabel,
            },
          });
          continue;
        }
      } catch (e) {
        console.warn("Failed to parse chart JSON:", e);
        // Fall through to regular paragraph handling
      }
    }

    const heading = headingMatch(line);
    if (heading) {
      const [, hashes, headingContent] = heading;
      if (hashes && headingContent) {
        blocks.push({ type: "heading", level: hashes.length, content: headingContent.trim() });
      }
      index += 1;
      continue;
    }

    if (unorderedMatch(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = unorderedMatch(lines[index]);
        if (!match) break;
        const [, itemContent] = match;
        if (itemContent) {
          items.push(itemContent.trim());
        }
        index += 1;
      }
      if (items.length) {
        blocks.push({ type: "unordered-list", items });
      }
      continue;
    }

    if (orderedMatch(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = orderedMatch(lines[index]);
        if (!match) break;
        const [, itemContent] = match;
        if (itemContent) {
          items.push(itemContent.trim());
        }
        index += 1;
      }
      if (items.length) {
        blocks.push({ type: "ordered-list", items });
      }
      continue;
    }

    const paragraphLines: string[] = [];
    while (index < lines.length) {
      const currentLine = lines[index];
      if (!currentLine.trim()) {
        break;
      }
      if (
        headingMatch(currentLine)
        || unorderedMatch(currentLine)
        || orderedMatch(currentLine)
        || dividerMatch(currentLine)
      ) {
        break;
      }
      paragraphLines.push(currentLine.trim());
      index += 1;
    }
    if (paragraphLines.length) {
      if (paragraphLines.length === 1) {
        const youtube = extractYoutubeDetails(paragraphLines[0]);
        if (youtube && paragraphLines[0].trim() === youtube.matchedText) {
          blocks.push({ type: "youtube", url: youtube.url, videoId: youtube.videoId });
          continue;
        }
      }
      blocks.push({ type: "paragraph", content: paragraphLines.join(" ").trim() });
    } else {
      index += 1;
    }
  }

  return blocks.length ? blocks : [{ type: "paragraph", content }];
};

const findNextInlineMatch = (source: string): InlineMatch | null => {
  let earliest: InlineMatch | null = null;
  INLINE_PATTERNS.forEach(({ type, regex }) => {
    regex.lastIndex = 0;
    const result = regex.exec(source);
    if (!result) return;
    if (!earliest || result.index < earliest.index) {
      earliest = { type, match: result, index: result.index };
    }
  });
  return earliest;
};

const renderInlineSegments = (
  text: string,
  keyPrefix: string,
  onYoutubeOpen?: (url: string) => void,
): React.ReactNode[] => {
  if (!text) {
    return [];
  }

  const nodes: React.ReactNode[] = [];
  let remaining = text;
  let partIndex = 0;

  while (remaining.length > 0) {
    const match = findNextInlineMatch(remaining);
    if (!match) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-${partIndex}`}>
          {remaining}
        </React.Fragment>,
      );
      break;
    }

    if (match.index > 0) {
      nodes.push(
        <React.Fragment key={`${keyPrefix}-${partIndex}`}>
          {remaining.slice(0, match.index)}
        </React.Fragment>,
      );
      partIndex += 1;
    }

    const matchedText = match.match[0];
    const nodeKey = `${keyPrefix}-${partIndex}`;

    switch (match.type) {
      case "bold": {
        const value = match.match[1]?.trim() ?? matchedText;
        nodes.push(
          <span key={nodeKey} className="markdown-bold">
            {renderInlineSegments(value, `${nodeKey}-bold`, onYoutubeOpen)}
          </span>,
        );
        break;
      }
      case "italic": {
        const value = match.match[1]?.trim() ?? matchedText;
        nodes.push(
          <span key={nodeKey} className="markdown-italic">
            {renderInlineSegments(value, `${nodeKey}-italic`, onYoutubeOpen)}
          </span>,
        );
        break;
      }
      case "button": {
        const label = match.match[1]?.trim() || "Button";
        const href = normalizeHref(match.match[2]);
        const youtube = extractYoutubeDetails(href);
        nodes.push(
          <a
            key={nodeKey}
            className="markdown-button"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={youtube && onYoutubeOpen ? (event) => {
              event.preventDefault();
              onYoutubeOpen(youtube.url);
            } : undefined}
          >
            {label}
          </a>,
        );
        break;
      }
      case "link": {
        const label = match.match[1]?.trim() || match.match[2]?.trim() || matchedText;
        const href = normalizeHref(match.match[2]);
        const youtube = extractYoutubeDetails(href);
        nodes.push(
          <a
            key={nodeKey}
            className="markdown-link"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={youtube && onYoutubeOpen ? (event) => {
              event.preventDefault();
              onYoutubeOpen(youtube.url);
            } : undefined}
          >
            {label}
          </a>,
        );
        break;
      }
      case "auto-link": {
        const href = normalizeHref(match.match[1]);
        const youtube = extractYoutubeDetails(href);
        nodes.push(
          <a
            key={nodeKey}
            className="markdown-link"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={youtube && onYoutubeOpen ? (event) => {
              event.preventDefault();
              onYoutubeOpen(youtube.url);
            } : undefined}
          >
            {href}
          </a>,
        );
        break;
      }
      case "email": {
        const email = match.match[0];
        nodes.push(
          <a
            key={nodeKey}
            className="markdown-link"
            href={`mailto:${email}`}
          >
            {email}
          </a>,
        );
        break;
      }
      default:
        nodes.push(
          <React.Fragment key={nodeKey}>
            {matchedText}
          </React.Fragment>,
        );
        break;
    }

    partIndex += 1;
    remaining = remaining.slice(match.index + matchedText.length);
  }

  return nodes;
};

const CHART_COLORS = [
  "#1782ff",
  "#e3f0ff",
  "#fd5050",
  "#105fc4",
  "#9cc9ff",
  "#0ea5e9",
];

const ChartBlock: React.FC<{ block: Extract<MarkdownBlock, { type: "chart" }> }> = ({ block }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { chartType, data = [], datasets = [], options } = block;
  const height = options?.height || 200;
  const colors = options?.colors || CHART_COLORS;
  const hasMultiSeries = datasets.length > 0;
  const shouldShowLegend = hasMultiSeries
    ? options?.showLegend !== false
    : options?.showLegend === true;
  const axisLabelStyle = { fill: "var(--text-medium, #6b7280)", fontSize: 11 };
  const xAxisLabel = options?.xLabel
    ? { value: options.xLabel, position: "insideBottom", offset: -4, style: axisLabelStyle }
    : undefined;
  const singleYAxisLabel = options?.yLabel
    ? { value: options.yLabel, angle: -90, position: "insideLeft", style: axisLabelStyle }
    : undefined;

  const formatValue = (val: number) => {
    const prefix = options?.valuePrefix || "";
    const suffix = options?.valueSuffix || "";
    return `${prefix}${val.toLocaleString()}${suffix}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="ai-chart-tooltip">
        <div className="ai-chart-tooltip-label">{label}</div>
        <div className="ai-chart-tooltip-value">
          {formatValue(payload[0].value)}
        </div>
      </div>
    );
  };

  const MultiTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="ai-chart-tooltip">
        <div className="ai-chart-tooltip-label">{label}</div>
        {payload.map((item: any) => (
          <div
            key={`${item.dataKey}-${item.name}`}
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: item.color || colors[0],
                display: "inline-block",
              }}
            />
            <span style={{ color: "var(--text-dark, #1f2937)", fontSize: 11 }}>
              {item.name}
            </span>
            <span className="ai-chart-tooltip-value">{formatValue(item.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const item = payload[0];
    return (
      <div className="ai-chart-tooltip">
        <div className="ai-chart-tooltip-label">{item.name}</div>
        <div className="ai-chart-tooltip-value">
          {formatValue(item.value)} ({item.payload.percent}%)
        </div>
      </div>
    );
  };

  const normalizedDatasets = datasets.map((dataset, index) => ({
    ...dataset,
    dataKey: `series-${index}`,
    color: dataset.color || colors[index % colors.length],
  }));

  const mergedData = normalizedDatasets.length
    ? (() => {
      const labelOrder: string[] = [];
      const merged = new Map<string, Record<string, any>>();

      normalizedDatasets.forEach((dataset) => {
        dataset.data.forEach((point) => {
          if (!point.label) return;
          if (!merged.has(point.label)) {
            merged.set(point.label, { label: point.label });
            labelOrder.push(point.label);
          }
          const target = merged.get(point.label);
          if (target) {
            target[dataset.dataKey] = point.value;
          }
        });
      });

      return labelOrder.map((label) => merged.get(label) || { label });
    })()
    : [];

  const leftAxisLabel = options?.yAxisLeftLabel ?? options?.yLabel;
  const rightAxisLabel = options?.yAxisRightLabel;
  const hasRightAxis = normalizedDatasets.some((dataset) => dataset.yAxis === "right");
  const hasLeftAxis = normalizedDatasets.some((dataset) => dataset.yAxis !== "right");

  const buildLegend = () => (
    shouldShowLegend ? (
      <Legend
        verticalAlign="bottom"
        height={36}
        formatter={(value) => (
          <span style={{ color: "var(--text-dark, #1f2937)", fontSize: 11 }}>{value}</span>
        )}
      />
    ) : null
  );

  const xAxisProps = {
    dataKey: "label",
    tick: { fill: "var(--text-medium, #6b7280)", fontSize: 11 },
    axisLine: { stroke: "var(--stroke-dark-grey, #e5e7eb)" },
    label: xAxisLabel,
  };

  const leftYAxisProps = {
    ...(hasMultiSeries ? { yAxisId: "left" as const } : {}),
    tick: { fill: "var(--text-medium, #6b7280)", fontSize: 11 },
    axisLine: { stroke: "var(--stroke-dark-grey, #e5e7eb)" },
    tickFormatter: (val: number) => formatValue(val),
    label: leftAxisLabel
      ? { value: leftAxisLabel, angle: -90, position: "insideLeft", style: axisLabelStyle }
      : singleYAxisLabel,
  };

  const rightYAxisProps = {
    yAxisId: "right" as const,
    orientation: "right" as const,
    tick: { fill: "var(--text-medium, #6b7280)", fontSize: 11 },
    axisLine: { stroke: "var(--stroke-dark-grey, #e5e7eb)" },
    tickFormatter: (val: number) => formatValue(val),
    label: rightAxisLabel
      ? { value: rightAxisLabel, angle: 90, position: "insideRight", style: axisLabelStyle }
      : undefined,
  };

  // Calculate percentages for pie chart
  const pieSource = data.length ? data : datasets[0]?.data || [];
  const pieDataWithPercent = chartType === "pie"
    ? (() => {
      const total = pieSource.reduce((sum, d) => sum + d.value, 0);
      return pieSource.map(d => ({
        ...d,
        name: d.label,
        percent: total > 0 ? ((d.value / total) * 100).toFixed(1) : "0",
      }));
    })()
    : [];

  const handleChartDownload = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const svg =
      container.querySelector("svg.recharts-surface")
      || Array.from(container.querySelectorAll("svg"))
        .find((node) => !(node.closest && node.closest(".ai-chart-download")));
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const widthAttr = Number(svg.getAttribute("width")) || rect.width;
    const heightAttr = Number(svg.getAttribute("height")) || rect.height;
    const chartWidth = Math.max(widthAttr, rect.width);
    const chartHeight = Math.max(heightAttr, rect.height);
    if (!chartWidth || !chartHeight) return;

    const legendEntries: Array<{ label: string; color: string }> = [];
    if (chartType === "pie") {
      const source = pieDataWithPercent.length ? pieDataWithPercent : pieSource;
      source.forEach((entry, idx) => {
        const label = (entry as any).name ?? entry.label ?? `Slice ${idx + 1}`;
        const color = (entry as any).color ?? colors[idx % colors.length];
        legendEntries.push({ label, color });
      });
    } else if (hasMultiSeries) {
      normalizedDatasets.forEach((dataset, idx) => {
        legendEntries.push({
          label: dataset.label,
          color: dataset.color || colors[idx % colors.length],
        });
      });
    } else if (shouldShowLegend) {
      legendEntries.push({ label: options?.yLabel || "Series", color: colors[0] });
    }

    const titleText = options?.title?.trim() || "";
    const padding = 16;
    const titleHeight = titleText ? 22 : 0;
    const legendLineHeight = 18;
    const legendHeight = legendEntries.length ? legendEntries.length * legendLineHeight + 8 : 0;

    const canvasWidth = Math.ceil(chartWidth + padding * 2);
    const canvasHeight = Math.ceil(
      padding
      + titleHeight
      + (titleText ? 6 : 0)
      + chartHeight
      + (legendHeight ? 10 + legendHeight : 0)
      + padding,
    );

    const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
    clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clonedSvg.setAttribute("width", `${chartWidth}`);
    clonedSvg.setAttribute("height", `${chartHeight}`);

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clonedSvg);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      const scale = window.devicePixelRatio || 1;
      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth * scale;
      canvas.height = canvasHeight * scale;
      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(url);
        return;
      }

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.scale(scale, scale);

      let cursorY = padding;
      if (titleText) {
        context.fillStyle = "#0f172a";
        context.font = "600 14px \"Inter\", \"Segoe UI\", Arial, sans-serif";
        context.textAlign = "center";
        context.fillText(titleText, canvasWidth / 2, cursorY + 14);
        cursorY += titleHeight + 6;
      }

      context.drawImage(image, padding, cursorY, chartWidth, chartHeight);
      cursorY += chartHeight;

      if (legendEntries.length) {
        cursorY += 10;
        context.textAlign = "left";
        context.font = "12px \"Inter\", \"Segoe UI\", Arial, sans-serif";
        legendEntries.forEach((entry, index) => {
          const y = cursorY + index * legendLineHeight;
          context.fillStyle = entry.color;
          context.fillRect(padding, y - 9, 12, 12);
          context.fillStyle = "#1f2937";
          context.fillText(entry.label, padding + 18, y + 2);
        });
        cursorY += legendHeight;
      }

      URL.revokeObjectURL(url);

      const pngUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      const safeTitle = (titleText || "chart")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "chart";
      link.href = pngUrl;
      link.download = `${safeTitle}.png`;
      link.click();
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      window.alert("Sorry, we couldn't export this chart right now.");
    };

    image.src = url;
  }, [chartType, colors, hasMultiSeries, normalizedDatasets, options?.title, options?.yLabel, pieDataWithPercent, pieSource, shouldShowLegend]);

  return (
    <div className="ai-chart-container" ref={containerRef}>
      <button
        type="button"
        className="ai-chart-download"
        onClick={handleChartDownload}
        aria-label="Download chart as PNG"
      >
        <img src="/assets/Download.svg" alt="" aria-hidden="true" />
      </button>
      {options?.title && (
        <div className="ai-chart-title">{options.title}</div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {chartType === "line" ? (
          hasMultiSeries ? (
            <LineChart data={mergedData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--stroke-dark-grey, #e5e7eb)" />
              <XAxis {...xAxisProps} />
              {hasLeftAxis && <YAxis {...leftYAxisProps} />}
              {hasRightAxis && <YAxis {...rightYAxisProps} />}
              <RechartsTooltip content={<MultiTooltip />} />
              {normalizedDatasets.map((dataset) => (
                <Line
                  key={dataset.dataKey}
                  type="monotone"
                  dataKey={dataset.dataKey}
                  name={dataset.label}
                  stroke={dataset.color}
                  strokeWidth={2}
                  dot={{ fill: dataset.color, r: 3 }}
                  activeDot={{ r: 5 }}
                  yAxisId={dataset.yAxis === "right" ? "right" : "left"}
                />
              ))}
              {buildLegend()}
            </LineChart>
          ) : (
            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--stroke-dark-grey, #e5e7eb)" />
              <XAxis {...xAxisProps} />
              <YAxis {...leftYAxisProps} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={colors[0]}
                strokeWidth={2}
                dot={{ fill: colors[0], r: 3 }}
                activeDot={{ r: 5 }}
              />
              {buildLegend()}
            </LineChart>
          )
        ) : chartType === "bar" ? (
          hasMultiSeries ? (
            <BarChart data={mergedData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--stroke-dark-grey, #e5e7eb)" />
              <XAxis {...xAxisProps} />
              {hasLeftAxis && <YAxis {...leftYAxisProps} />}
              {hasRightAxis && <YAxis {...rightYAxisProps} />}
              <RechartsTooltip content={<MultiTooltip />} />
              {normalizedDatasets.map((dataset) => (
                <Bar
                  key={dataset.dataKey}
                  dataKey={dataset.dataKey}
                  name={dataset.label}
                  fill={dataset.color}
                  radius={[4, 4, 0, 0]}
                  yAxisId={dataset.yAxis === "right" ? "right" : "left"}
                />
              ))}
              {buildLegend()}
            </BarChart>
          ) : (
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--stroke-dark-grey, #e5e7eb)" />
              <XAxis {...xAxisProps} />
              <YAxis {...leftYAxisProps} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((entry, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={entry.color || colors[idx % colors.length]}
                  />
                ))}
              </Bar>
              {buildLegend()}
            </BarChart>
          )
        ) : (
          <PieChart>
            <RechartsTooltip content={<PieTooltip />} />
            <Pie
              data={pieDataWithPercent}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={70}
              innerRadius={30}
              paddingAngle={2}
            >
              {pieDataWithPercent.map((entry, idx) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={entry.color || colors[idx % colors.length]}
                />
              ))}
            </Pie>
            {(options?.showLegend !== false && pieDataWithPercent.length > 0) && (
              <Legend
                verticalAlign="bottom"
                height={36}
                formatter={(value) => (
                  <span style={{ color: "var(--text-dark, #1f2937)", fontSize: 11 }}>{value}</span>
                )}
              />
            )}
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

// Editable email-draft card. The body is contentEditable so the operator can tweak wording in the
// browser; edits live only in the DOM (never persisted to the message/data) and are picked up when
// they copy. "Send" is intentionally disabled until wired up.
const EmailDraftCard: React.FC<{ to: string; subject: string; body: string }> = ({
  to,
  subject,
  body,
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [copied, setCopied] = useState(false);
  const copyResetRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (copyResetRef.current) {
      window.clearTimeout(copyResetRef.current);
    }
  }, []);

  const handleCopy = () => {
    const currentBody = bodyRef.current?.innerText ?? body;
    const copyText = `${to ? `To: ${to}\n` : ""}${subject ? `Subject: ${subject}\n\n` : ""}${currentBody}`;
    void navigator.clipboard?.writeText(copyText);
    setCopied(true);
    if (copyResetRef.current) {
      window.clearTimeout(copyResetRef.current);
    }
    copyResetRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const handleEdit = () => {
    const el = bodyRef.current;
    if (!el) {
      return;
    }
    el.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  return (
    <div className="ai-chat-email">
      {(to || subject) && (
        <div className="ai-chat-email-head">
          {to && <p className="ai-chat-email-row"><strong>To:</strong> {to}</p>}
          {subject && <p className="ai-chat-email-row"><strong>Subject:</strong> {subject}</p>}
        </div>
      )}
      {body && (
        <div
          ref={bodyRef}
          className="ai-chat-email-body"
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
        >
          {body}
        </div>
      )}
      <div className="ai-chat-email-actions">
        <button
          type="button"
          className="ai-chat-email-copy"
          onClick={handleCopy}
        >
          <span
            className="ai-chat-email-icon"
            aria-hidden="true"
            style={{
              WebkitMaskImage: `url(/assets/ai/email/${copied ? "copy-check" : "copy"}.svg)`,
              maskImage: `url(/assets/ai/email/${copied ? "copy-check" : "copy"}.svg)`,
            }}
          />
          {copied ? "Copied" : "Copy Email"}
        </button>
        <button
          type="button"
          className="ai-chat-email-edit"
          onClick={handleEdit}
        >
          <span
            className="ai-chat-email-icon"
            aria-hidden="true"
            style={{
              WebkitMaskImage: "url(/assets/ai/email/edit.svg)",
              maskImage: "url(/assets/ai/email/edit.svg)",
            }}
          />
          Edit
        </button>
        <button
          type="button"
          className="ai-chat-email-send"
          disabled
          title="Sending isn't available yet"
        >
          <span
            className="ai-chat-email-icon"
            aria-hidden="true"
            style={{
              WebkitMaskImage: "url(/assets/ai/email/send.svg)",
              maskImage: "url(/assets/ai/email/send.svg)",
            }}
          />
          Send
        </button>
      </div>
    </div>
  );
};

// The whole payload arrives at once, so the type-on animation only types the narrative prose; tables
// and charts (which look like raw markdown mid-type) are dropped here and shown as skeletons instead.
const getAnimatableProse = (content: string): string => {
  const blocks = parseMarkdownBlocks(content);
  const parts: string[] = [];
  blocks.forEach((block) => {
    switch (block.type) {
      case "paragraph":
      case "heading":
        parts.push(block.content);
        break;
      case "unordered-list":
      case "ordered-list":
        parts.push(block.items.join("\n"));
        break;
      case "youtube":
        parts.push(block.url);
        break;
      // table / chart / divider → omitted (rendered as skeletons, then real blocks)
      default:
        break;
    }
  });
  return parts.join("\n\n").trim();
};

// Block types in the message that should appear as skeletons while the prose types in.
const getSkeletonBlocks = (content: string): MarkdownBlock[] =>
  parseMarkdownBlocks(content).filter((block) => block.type === "table" || block.type === "chart");

const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => {
  const safeRows = Math.min(Math.max(rows, 2), 5);
  return (
    <div className="ai-chat-skeleton-table">
      <div className="skeleton-block ai-chat-skeleton-row ai-chat-skeleton-row--head" />
      {Array.from({ length: safeRows }).map((_, index) => (
        <div key={index} className="skeleton-block ai-chat-skeleton-row" />
      ))}
    </div>
  );
};

const ChartSkeleton: React.FC = () => (
  <div className="skeleton-block ai-chat-skeleton-chart" />
);

const CardSkeleton: React.FC = () => (
  <div className="ai-chat-skeleton-card">
    <div className="skeleton-block ai-chat-skeleton-line ai-chat-skeleton-line--title" />
    <div className="skeleton-block ai-chat-skeleton-line" />
    <div className="skeleton-block ai-chat-skeleton-line ai-chat-skeleton-line--short" />
  </div>
);

// Skeleton stand-ins shown under the typing prose for any block content in the message (tables,
// charts, an agent card, or an email draft), so nothing pops in raw or instantly.
const MessageBlockSkeletons: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const blocks = getSkeletonBlocks(message.content);
  const hasAgent = Boolean(message.rawJson?.agent_event);
  const hasEmail = Boolean(message.rawJson?.email_draft);
  if (blocks.length === 0 && !hasAgent && !hasEmail) {
    return null;
  }
  return (
    <div className="ai-chat-skeletons skeleton-stack">
      {blocks.map((block, index) => (
        block.type === "chart"
          ? <ChartSkeleton key={`sk-${index}`} />
          : <TableSkeleton key={`sk-${index}`} rows={(block as any).rows?.length ?? 3} />
      ))}
      {hasAgent && <CardSkeleton />}
      {hasEmail && <CardSkeleton />}
    </div>
  );
};

// Custom, readable preview for the advanced-pricing agent. The generic table gets unwieldy (up to 35
// time-slot rows); this groups slots by day and shows a git-diff (old red → new green) per slot.
const ADV_DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const ADV_DAY_LABELS: Record<string, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};
const ADV_DAY_ALIASES: Record<string, string> = {
  mon: "monday", monday: "monday",
  tue: "tuesday", tues: "tuesday", tuesday: "tuesday",
  wed: "wednesday", weds: "wednesday", wednesday: "wednesday",
  thu: "thursday", thur: "thursday", thurs: "thursday", thursday: "thursday",
  fri: "friday", friday: "friday",
  sat: "saturday", saturday: "saturday",
  sun: "sunday", sunday: "sunday",
};

const parseAdvDayLabel = (label: string): { dayKey: string | null; window: string } => {
  const trimmed = (label ?? "").trim();
  if (!trimmed) {
    return { dayKey: null, window: "" };
  }
  const match = trimmed.match(/^([A-Za-z]+)[.,]?\s*(.*)$/);
  if (!match) {
    return { dayKey: null, window: trimmed };
  }
  const dayKey = ADV_DAY_ALIASES[match[1].toLowerCase()] ?? null;
  if (!dayKey) {
    return { dayKey: null, window: trimmed };
  }
  return { dayKey, window: match[2].trim() || "All day" };
};

// Tidy a schedule value for display. Robust to whatever the model emits: strips the time range (the
// slot's window column already shows it), collapses any $0 public window to "Free", spaces the
// block-mode words, and drops the redundant "Set Time" label (the price speaks for itself).
const prettifyAdvValue = (raw: string): string => {
  let v = (raw ?? "").trim();
  if (!v || v === "—") {
    return v;
  }

  // Normalize common phrasings first so the rules below catch more of what the model might write.
  v = v
    .replace(/(\d+(?:\.\d+)?)\s*dollars?\b/gi, "$$$1")                  // "2 dollars" → "$2"
    .replace(/\bcapped?\s*(?:at)?\b|\bup\s*to\b|\bmaximum\b/gi, "max")  // cap / up to / maximum → max
    .replace(/\bper\s*hour\b|\ban\s*hour\b|\bhourly\b|\/\s*hour\b/gi, "/hr");

  // Remove duplicate time ranges like "09–17", "09:00-17:00", "9am-5pm" (kept in the window column).
  v = v.replace(/\b\d{1,2}(?::\d{2})?\s*(?:am|pm)?\s*[–—-]\s*\d{1,2}(?::\d{2})?\s*(?:am|pm)?\b/gi, " ");

  // A $0 public window (or "free"/"no charge") is just "Free"; permit-only is registry-only, left alone.
  const isPermit = /permit/i.test(v);
  const moneyless = v.replace(/set[\s-]*time|all[\s-]*day|free|permit[\s-]*only|\/hr|[(),]/gi, " ").trim();
  if (!isPermit && (/\b(?:free|no\s*charge|no\s*cost)\b/i.test(v) || /^\$?0(?:\.0+)?$/.test(moneyless))) {
    return "Free";
  }

  v = v
    .replace(/permit[\s-]*only|permit[\s-]*enforcement/gi, "Permit Only")
    .replace(/all[\s-]*day/gi, "All Day")
    .replace(/set[\s-]*time/gi, "")              // the price already conveys a paid window
    .replace(/\(\s*\)/g, " ")                    // empty parens left after removals
    .replace(/\s*,\s*,/g, ",")
    .replace(/^[\s,]+|[\s,]+$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

  return v || raw.trim();
};

type AdvSlot = { window: string; current: string; proposed: string; changed: boolean };

const AdvancedPricingPreview: React.FC<{ preview: any }> = ({ preview }) => {
  const rows: any[] = Array.isArray(preview?.rows) ? preview.rows : [];
  const currency = preview?.currency;
  const groups = new Map<string, AdvSlot[]>();
  const otherSlots: AdvSlot[] = [];

  rows.forEach((row) => {
    const { dayKey, window } = parseAdvDayLabel(typeof row?.label === "string" ? row.label : "");
    const slot: AdvSlot = {
      window,
      current: prettifyAdvValue(formatAgentValue(row?.current, currency)),
      proposed: prettifyAdvValue(formatAgentValue(row?.proposed, currency)),
      changed: Boolean(row?.changed),
    };
    if (dayKey) {
      const list = groups.get(dayKey) ?? [];
      list.push(slot);
      groups.set(dayKey, list);
    } else {
      otherSlots.push(slot);
    }
  });

  const orderedDays = ADV_DAY_ORDER.filter((day) => groups.has(day));
  if (orderedDays.length === 0 && otherSlots.length === 0) {
    return null;
  }

  const renderSlot = (slot: AdvSlot, key: string) => {
    const showOld = slot.changed && slot.current && slot.current !== "—" && slot.current !== slot.proposed;
    return (
      <div key={key} className={`ai-chat-adv-slot${slot.changed ? " is-changed" : ""}`}>
        <span className="ai-chat-adv-time">{slot.window}</span>
        <span className="ai-chat-adv-rules">
          {showOld && <span className="ai-chat-adv-old">{slot.current}</span>}
          <span className={`ai-chat-adv-new${slot.changed ? " is-changed" : ""}`}>
            {slot.proposed && slot.proposed !== "—" ? slot.proposed : slot.current}
          </span>
        </span>
      </div>
    );
  };

  return (
    <div className="ai-chat-adv">
      {orderedDays.map((dayKey) => {
        const slots = groups.get(dayKey) ?? [];
        const dayChanged = slots.some((slot) => slot.changed);
        return (
          <div key={dayKey} className={`ai-chat-adv-day${dayChanged ? " is-changed" : ""}`}>
            <span className="ai-chat-adv-day-name">{ADV_DAY_LABELS[dayKey]}</span>
            <div className="ai-chat-adv-slots">
              {slots.map((slot, index) => renderSlot(slot, `${dayKey}-${index}`))}
            </div>
          </div>
        );
      })}
      {otherSlots.length > 0 && (
        <div className="ai-chat-adv-day">
          <div className="ai-chat-adv-slots">
            {otherSlots.map((slot, index) => renderSlot(slot, `other-${index}`))}
          </div>
        </div>
      )}
    </div>
  );
};

const renderMarkdownContent = (content: string, onYoutubeOpen: (url: string) => void) => {
  const blocks = parseMarkdownBlocks(content);
  if (!blocks.length) {
    return (
      <p className="markdown-paragraph">
        {renderInlineSegments(content, "block-0", onYoutubeOpen)}
      </p>
    );
  }

  return blocks.map((block, index) => {
    const key = `block-${index}`;
    switch (block.type) {
      case "heading": {
        const inlineVideos = findYoutubeLinks(block.content);
        if (!inlineVideos.length) {
          return (
            <p key={key} className={`markdown-heading markdown-heading--${block.level}`}>
              {renderInlineSegments(block.content, `${key}-heading`, onYoutubeOpen)}
            </p>
          );
        }
        return (
          <React.Fragment key={key}>
            <p className={`markdown-heading markdown-heading--${block.level}`} key={`${key}-text`}>
              {renderInlineSegments(block.content, `${key}-heading`, onYoutubeOpen)}
            </p>
            {inlineVideos.map((video) => renderYoutubePreview(video.videoId, video.url, `${key}-yt-${video.videoId}`, onYoutubeOpen))}
          </React.Fragment>
        );
      }
      case "unordered-list": {
        const seen = new Set<string>();
        const inlineVideos = block.items
          .flatMap((item) => findYoutubeLinks(item))
          .filter((video) => {
            if (seen.has(video.videoId)) {
              return false;
            }
            seen.add(video.videoId);
            return true;
          });
        if (!inlineVideos.length) {
          return (
            <ul key={key} className="markdown-list">
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-item-${itemIndex}`}>
                  {renderInlineSegments(item, `${key}-item-${itemIndex}`, onYoutubeOpen)}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <React.Fragment key={key}>
            <ul className="markdown-list" key={`${key}-list`}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-item-${itemIndex}`}>
                  {renderInlineSegments(item, `${key}-item-${itemIndex}`, onYoutubeOpen)}
                </li>
              ))}
            </ul>
            {inlineVideos.map((video) => renderYoutubePreview(video.videoId, video.url, `${key}-yt-${video.videoId}`, onYoutubeOpen))}
          </React.Fragment>
        );
      }
      case "ordered-list": {
        const seen = new Set<string>();
        const inlineVideos = block.items
          .flatMap((item) => findYoutubeLinks(item))
          .filter((video) => {
            if (seen.has(video.videoId)) {
              return false;
            }
            seen.add(video.videoId);
            return true;
          });
        if (!inlineVideos.length) {
          return (
            <ol key={key} className="markdown-list markdown-list--ordered">
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-item-${itemIndex}`}>
                  {renderInlineSegments(item, `${key}-item-${itemIndex}`, onYoutubeOpen)}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <React.Fragment key={key}>
            <ol className="markdown-list markdown-list--ordered" key={`${key}-list`}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-item-${itemIndex}`}>
                  {renderInlineSegments(item, `${key}-item-${itemIndex}`, onYoutubeOpen)}
                </li>
              ))}
            </ol>
            {inlineVideos.map((video) => renderYoutubePreview(video.videoId, video.url, `${key}-yt-${video.videoId}`, onYoutubeOpen))}
          </React.Fragment>
        );
      }
      case "table": {
        const columnCount = Math.max(
          block.header.length,
          ...block.rows.map((row) => row.length),
        );
        const alignFor = (cellIndex: number) => block.alignments[cellIndex] || "left";
        return (
          <div key={key} className="markdown-table-wrapper">
            <table className="markdown-table">
              <thead>
                <tr>
                  {Array.from({ length: columnCount }).map((_, cellIndex) => (
                    <th
                      key={`${key}-header-${cellIndex}`}
                      style={{ textAlign: alignFor(cellIndex) }}
                    >
                      {renderInlineSegments(
                        block.header[cellIndex] ?? "",
                        `${key}-header-${cellIndex}`,
                        onYoutubeOpen,
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              {block.rows.length > 0 && (
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={`${key}-row-${rowIndex}`}>
                      {Array.from({ length: columnCount }).map((_, cellIndex) => {
                        const cellValue = row[cellIndex] ?? "";
                        const cellKey = `${key}-row-${rowIndex}-cell-${cellIndex}`;
                        const plateCol = isPlateHeader(block.header[cellIndex]);
                        const statusChip = !plateCol ? renderStatusChip(cellValue, cellKey) : null;
                        return (
                          <td key={cellKey} style={{ textAlign: alignFor(cellIndex) }}>
                            {plateCol && looksLikePlate(cellValue)
                              ? renderPlateBadge(cellValue, cellKey)
                              : (statusChip ?? renderInlineSegments(cellValue, cellKey, onYoutubeOpen))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        );
      }
      case "youtube":
        return renderYoutubePreview(block.videoId, block.url, key, onYoutubeOpen);
      case "divider":
        return (
          <hr key={key} className="markdown-divider" />
        );
      case "chart":
        return <ChartBlock key={key} block={block} />;
      case "paragraph":
      default:
        if (!block.content) {
          return null;
        }
        const inlineVideos = findYoutubeLinks(block.content);
        if (!inlineVideos.length) {
          return (
            <p key={key} className="markdown-paragraph">
              {renderInlineSegments(block.content, `${key}-paragraph`, onYoutubeOpen)}
            </p>
          );
        }
        return (
          <React.Fragment key={key}>
            <p className="markdown-paragraph" key={`${key}-text`}>
              {renderInlineSegments(block.content, `${key}-paragraph`, onYoutubeOpen)}
            </p>
            {inlineVideos.map((video) => renderYoutubePreview(video.videoId, video.url, `${key}-yt-${video.videoId}`, onYoutubeOpen))}
          </React.Fragment>
        );
    }
  });
};

const formatTimestamp = (value?: string | null) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const buildTempId = () => `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

type ChatAttachment = {
  id: string;
  kind: "image" | "sheet";
  name: string;
  dataUrl?: string; // images: data:image/...;base64,...
  text?: string;    // sheets: converted CSV/text
};

const ATTACHMENT_SHEET_MAX_CHARS = 20000;
const SHEET_FILE_REGEX = /\.(csv|xlsx|xls)$/i;

const isSheetFile = (file: File) =>
  SHEET_FILE_REGEX.test(file.name)
  || file.type.includes("csv")
  || file.type.includes("sheet")
  || file.type.includes("excel");

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Convert an Excel/CSV file to plain text (one CSV block per sheet) client-side, so it can be sent
// as part of the message content. SheetJS handles both .xlsx/.xls and .csv.
const sheetFileToText = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const parts: string[] = [];
  workbook.SheetNames.forEach((name: string) => {
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[name]);
    if (csv.trim()) {
      parts.push(`# Sheet: ${name}\n${csv}`);
    }
  });
  let text = parts.join("\n\n");
  if (text.length > ATTACHMENT_SHEET_MAX_CHARS) {
    text = `${text.slice(0, ATTACHMENT_SHEET_MAX_CHARS)}\n…(truncated)`;
  }
  return text;
};

const Chat: React.FC<ChatProps> = ({ isMobile = false, isMobileOpen = false, onMobileVisibilityChange, defaultOpen = true, maxWidth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lotId } = useParams<{ lotId: string }>();
  const { user } = useUser();
  const { isMultiLotMode, clearToSingleLot, getPrimaryLot } = useMultiLot();
  const queryClient = useQueryClient();
  const [showMultiLotWarning, setShowMultiLotWarning] = useState(false);
  const [visibility, setVisibility] = useState<ChatVisibility>(() => {
    if (typeof window === "undefined") {
      return defaultOpen ? "open" : "closed";
    }
    const stored = window.localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored === "closed" || stored === "open") {
      return stored;
    }
    return defaultOpen ? "open" : "closed";
  });
  const [chatWidth, setChatWidth] = useState<number>(() => {
    if (typeof window === "undefined") {
      return DEFAULT_OPEN_WIDTH;
    }
    const stored = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY) || "");
    if (Number.isFinite(stored)) {
      return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(stored)));
    }
    return DEFAULT_OPEN_WIDTH;
  });
  const [mode, setModeState] = useState<ChatMode>("agent");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<AiConversationSummary[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [rateInfo, setRateInfo] = useState<AiRateInfo | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyMenuOpen, setHistoryMenuOpen] = useState(false);
  const [isAnimatingResponse, setIsAnimatingResponse] = useState(false);
  const [messageAnimations, setMessageAnimations] = useState<Record<string, string>>({});
  const [confirmingTasks, setConfirmingTasks] = useState<Record<string, boolean>>({});
  const [cancellingTasks, setCancellingTasks] = useState<Record<string, boolean>>({});
  const [sessionKey, setSessionKey] = useState<string | null>(() => (
    typeof window === "undefined" ? null : getChatSessionKey()
  ));
  const [isAgentConfirmOpen, setIsAgentConfirmOpen] = useState(false);
  const [lastAgentConfirmAt, setLastAgentConfirmAt] = useState<number>(() => {
    if (typeof window === "undefined") {
      return 0;
    }
    const stored = window.localStorage.getItem(AGENT_CONFIRM_STORAGE_KEY);
    const parsed = stored ? Number(stored) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  });
  const [thinkingWord, setThinkingWord] = useState<string>(() => pickRandomThinkingWord(CHAT_THINKING_WORDS));
  const [thinkingDotsIndex, setThinkingDotsIndex] = useState(0);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const preWalkthroughWidthRef = useRef<number | null>(null);
  const preWalkthroughVisibilityRef = useRef<ChatVisibility | null>(null);

  const messageEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const loadingOlderRef = useRef(false);
  const [videoModal, setVideoModal] = useState({ isOpen: false, url: "", title: "" });
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingUserMessageIdRef = useRef<string | null>(null);
  const pendingConversationIdRef = useRef<string | null>(null);

  // Agent tagging state
  const [taggedAgents, setTaggedAgents] = useState<AiAgentNameItem[]>([]);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [availableAgents, setAvailableAgents] = useState<AiAgentNameItem[]>([]);
  // Track which agents were auto-tagged (so manual tags are preserved on route change)
  const autoTaggedAgentKeysRef = useRef<Set<string>>(new Set());
  const [isAgentPickerOpen, setIsAgentPickerOpen] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [atTriggerIndex, setAtTriggerIndex] = useState<number | null>(null);
  const [pendingAgentSelection, setPendingAgentSelection] = useState<{
    agent: AiAgentNameItem;
    nextValue: string;
    caretPosition: number;
  } | null>(null);
  const agentPickerRef = useRef<HTMLDivElement | null>(null);
  const agentRefreshTimeoutRef = useRef<number | null>(null);

  const animatingMessageRef = useRef<string | null>(null);
  const skipNextConversationLoadRef = useRef(false);
  const animationIntervalRef = useRef<number | null>(null);
  const animationTimeoutRef = useRef<number | null>(null);
  const thinkingWordTimeoutRef = useRef<number | null>(null);
  const thinkingDotsIntervalRef = useRef<number | null>(null);
  const sessionInfoRef = useRef<{ sessionKey: string | null; lotId: string | null }>({
    sessionKey: null,
    lotId: null,
  });
  const pendingModeRef = useRef<ChatMode | null>(null);

  // Track component mount status for safe async state updates
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearAnimationTimers = useCallback(() => {
    if (animationIntervalRef.current) {
      window.clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    if (animationTimeoutRef.current) {
      window.clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
  }, []);

  const clearThinkingTimers = useCallback(() => {
    if (thinkingWordTimeoutRef.current) {
      window.clearTimeout(thinkingWordTimeoutRef.current);
      thinkingWordTimeoutRef.current = null;
    }
    if (thinkingDotsIntervalRef.current) {
      window.clearInterval(thinkingDotsIntervalRef.current);
      thinkingDotsIntervalRef.current = null;
    }
  }, []);

  const refreshPageAfterAgentConfirm = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (agentRefreshTimeoutRef.current) {
      window.clearTimeout(agentRefreshTimeoutRef.current);
    }
    agentRefreshTimeoutRef.current = window.setTimeout(() => {
      agentRefreshTimeoutRef.current = null;
      window.dispatchEvent(new CustomEvent(AGENT_ACTION_APPLIED_EVENT, {
        detail: {
          path: location.pathname,
          lotId,
        },
      }));
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (!lotId) return false;
          const key = query.queryKey;
          return Array.isArray(key) && key.includes(lotId);
        },
      }).catch((error) => {
        // Log but don't block - query invalidation failure is not critical
        console.warn("Query invalidation failed:", error);
      });
    }, 400);
  }, [location.pathname, lotId, queryClient]);

  const resetMessageAnimations = useCallback(() => {
    clearAnimationTimers();
    animatingMessageRef.current = null;
    setIsAnimatingResponse(false);
    setMessageAnimations({});
  }, [clearAnimationTimers]);

  const greetingName = useMemo(() => {
    // Try to get a first name from user object
    // Note: Backend may provide first_name in addition to the typed 'name' field
    const userRecord = user as Record<string, unknown> | null;
    const firstName = userRecord?.first_name;
    const name = user?.name;

    const candidates = [
      typeof firstName === "string" ? firstName : undefined,
      typeof name === "string" ? name : undefined,
    ].filter((v): v is string => typeof v === "string" && v.trim().length > 0);

    const raw = candidates[0];
    if (!raw) {
      return "there";
    }

    return raw.trim().split(/\s+/)[0];
  }, [user]);

  const isMobileMode = Boolean(isMobile);

  useEffect(() => {
    if (!isMobileMode) {
      localStorage.setItem(VISIBILITY_STORAGE_KEY, visibility);
    }
  }, [visibility, isMobileMode]);

  // Collapse chat when multi-lot mode is active
  useEffect(() => {
    if (isMultiLotMode) {
      setVisibility("closed");
      if (isMobileMode) {
        onMobileVisibilityChange?.(false);
      }
    }
  }, [isMultiLotMode, isMobileMode, onMobileVisibilityChange]);

  const renderVisibility: ChatVisibility = isMobileMode
    ? (isMobileOpen ? "open" : "closed")
    : visibility;
  const isClosed = renderVisibility === "closed";

  // Let other surfaces (e.g. the dashboard tutorial button) react to the chat opening/closing.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(AI_CHAT_VISIBILITY_EVENT, { detail: { open: !isClosed } }),
    );
  }, [isClosed]);

  // Pre-warm the lot-context snapshot when the chat is open, so the first message isn't slow.
  // Once per lot (backend caches it ~12 min); re-warms if the lot changes.
  const warmedLotRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lotId || isClosed || warmedLotRef.current === lotId) {
      return;
    }
    warmedLotRef.current = lotId;
    void aiChatService.warmContext(lotId);
  }, [lotId, isClosed]);

  const effectiveMaxWidth = maxWidth !== undefined ? Math.min(MAX_WIDTH, maxWidth) : MAX_WIDTH;
  const normalizedWidth = Math.max(MIN_WIDTH, Math.min(effectiveMaxWidth, Math.round(chatWidth)));
  const isCompact = isMobileMode ? false : normalizedWidth < 300;
  const showRateLimit = !isCompact;
  const effectiveWidth = isClosed ? CLOSED_WIDTH : normalizedWidth;
  const chatInlineStyle = isMobileMode
    ? undefined
    : { width: `${effectiveWidth}px`, minWidth: `${effectiveWidth}px` };
  const thinkingWordPool = useMemo(
    () => (mode === "agent" ? THINKING_WORDS : CHAT_THINKING_WORDS),
    [mode],
  );

  const remainingUses = useMemo(() => {
    if (!rateInfo) return null;
    return Math.max(rateInfo.quota - rateInfo.used, 0);
  }, [rateInfo]);

  const canSend = (inputValue.trim().length > 0 || attachments.length > 0)
    && !isSending
    && (remainingUses === null || remainingUses > 0)
    && Boolean(lotId);

  const showThinkingBubble = isSending || isAnimatingResponse;
  const thinkingDots = THINKING_DOTS[thinkingDotsIndex] ?? THINKING_DOTS[0];

  const sizeIcon = isClosed
    ? "/assets/ai/ChatClosed.svg"
    : normalizedWidth >= LARGE_THRESHOLD
      ? "/assets/ai/ChatLarge.svg"
      : "/assets/ai/ChatSmall.svg";

  const adjustComposerHeight = (textarea: HTMLTextAreaElement) => {
    const lineHeight = 24;
    const maxHeight = lineHeight * 3;
    textarea.style.height = "auto";
    const nextHeight = Math.min(maxHeight, textarea.scrollHeight);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  };

  const hasRecentAgentConsent = useCallback(() => {
    if (!lastAgentConfirmAt) {
      return false;
    }
    return (Date.now() - lastAgentConfirmAt) < AGENT_CONFIRM_COOLDOWN_MS;
  }, [lastAgentConfirmAt]);

  const fetchAgentNames = useCallback(async () => {
    try {
      const agents = await aiChatService.getAgentNames();
      setAvailableAgents(agents);
    } catch (error) {
      console.error("Failed to load agent names", error);
    }
  }, []);

  // Auto-tag agents based on current route (only in agent mode, not during walkthrough)
  useEffect(() => {
    // Suppress auto-tagging while walkthrough is managing its own tagged agents
    if (walkthroughActive) return;
    if (mode !== "agent" || availableAgents.length === 0) {
      return;
    }

    // Find which route pattern matches the current path
    const currentPath = location.pathname;
    let matchedAgentKeys: string[] = [];

    for (const [routePattern, agentKeys] of Object.entries(ROUTE_AUTO_TAG_AGENTS)) {
      if (currentPath.includes(routePattern)) {
        matchedAgentKeys = agentKeys;
        break;
      }
    }

    const prevAutoTagKeys = autoTaggedAgentKeysRef.current;
    const newAutoTagKeys = new Set(matchedAgentKeys);

    // Remove old auto-tagged agents that are no longer relevant, add new ones
    setTaggedAgents((prev) => {
      // Keep manually tagged agents and auto-tags that are still valid
      const filtered = prev.filter((agent) => {
        const wasAutoTagged = prevAutoTagKeys.has(agent.agentKey);
        const stillAutoTagged = newAutoTagKeys.has(agent.agentKey);
        // Keep if: not auto-tagged (manual), or still relevant for current route
        return !wasAutoTagged || stillAutoTagged;
      });

      // Add new auto-tagged agents that aren't already present
      const agentsToAdd = matchedAgentKeys
        .filter((key) => !filtered.some((a) => a.agentKey === key))
        .map((key) => availableAgents.find((a) => a.agentKey === key))
        .filter((agent): agent is AiAgentNameItem => agent !== undefined);

      if (agentsToAdd.length === 0 && filtered.length === prev.length) {
        return prev; // No changes needed
      }

      return [...filtered, ...agentsToAdd];
    });

    // Update the ref with the new auto-tag keys
    autoTaggedAgentKeysRef.current = newAutoTagKeys;
  }, [location.pathname, mode, availableAgents, walkthroughActive]);

  // Resolve walkthrough-requested tagged-agent keys once availableAgents are loaded
  useEffect(() => {
    const resolve = () => {
      const pendingKeys = (window as any).__walkthroughPendingAgentKeys as string[] | null;
      if (!pendingKeys || availableAgents.length === 0) return;
      const resolved = pendingKeys
        .map((key) => availableAgents.find((a) => a.agentKey === key))
        .filter((a): a is AiAgentNameItem => a !== undefined);
      if (resolved.length > 0) {
        setTaggedAgents(resolved);
        (window as any).__walkthroughPendingAgentKeys = null;
      }
    };
    resolve();
    window.addEventListener("walkthrough-resolve-agents", resolve);
    return () => window.removeEventListener("walkthrough-resolve-agents", resolve);
  }, [availableAgents]);

  const applyAgentSelection = useCallback((selection: {
    agent: AiAgentNameItem;
    nextValue: string;
    caretPosition: number;
  }) => {
    if (!taggedAgents.some((a) => a.agentKey === selection.agent.agentKey)) {
      setTaggedAgents((prev) => [...prev, selection.agent]);
    }

    setInputValue(selection.nextValue);

    window.requestAnimationFrame(() => {
      if (inputRef.current) {
        adjustComposerHeight(inputRef.current);
        inputRef.current.setSelectionRange(selection.caretPosition, selection.caretPosition);
        inputRef.current.focus();
      }
    });
  }, [adjustComposerHeight, taggedAgents]);

  const handleAgentModeConfirm = useCallback(() => {
    const shouldSwitchToAgent = pendingModeRef.current === "agent" || Boolean(pendingAgentSelection);
    if (shouldSwitchToAgent) {
      setModeState("agent");
      const timestamp = Date.now();
      setLastAgentConfirmAt(timestamp);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(AGENT_CONFIRM_STORAGE_KEY, String(timestamp));
      }
    }

    if (pendingAgentSelection) {
      applyAgentSelection(pendingAgentSelection);
      setPendingAgentSelection(null);
    }

    pendingModeRef.current = null;
    setIsAgentConfirmOpen(false);
  }, [applyAgentSelection, pendingAgentSelection]);

  const handleAgentModeCancel = useCallback(() => {
    pendingModeRef.current = null;
    setPendingAgentSelection(null);
    setIsAgentConfirmOpen(false);
  }, []);

  const fetchRateInfo = useCallback(async () => {
    if (!lotId) {
      setRateInfo(null);
      return;
    }
    try {
      const info = await aiChatService.getRateLimit(lotId);
      setRateInfo(info);
    } catch (error) {
      console.error("Failed to load AI rate info", error);
    }
  }, [lotId]);

  const fetchConversations = useCallback(async () => {
    if (!lotId) {
      setConversations([]);
      return;
    }
    setIsLoadingHistory(true);
    try {
      const items = await aiChatService.getConversationsByLot(lotId, {
        limit: HISTORY_LIMIT,
        status: "open",
      });
      setConversations(items || []);
    } catch (error) {
      console.error("Failed to load AI conversations", error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [lotId]);

  const mapMessages = useCallback(
    (items: AiMessageItem[]): ChatMessage[] => items.map((item) => ({
      id: item.messageId,
      role: item.role === "assistant" ? "assistant" : "user",
      content: extractMarkdown(item.content, item.contentJson),
      createdOn: item.createdOn,
      animate: false,
      rawJson: item.contentJson,
    })),
    [],
  );

  const loadConversationMessages = useCallback(async (targetConversationId: string) => {
    setIsLoadingMessages(true);
    resetMessageAnimations();
    setMessages([]);
    setOlderCursor(null);
    try {
      const page = await aiChatService.getMessages(targetConversationId, {
        limit: MESSAGE_PAGE_LIMIT,
      });
      const mapped = mapMessages(page.items || []);
      setMessages(mapped);
      setOlderCursor(page.previousBefore ?? null);
      window.requestAnimationFrame(() => {
        messageEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    } catch (error) {
      console.error("Failed to load conversation messages", error);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [mapMessages, resetMessageAnimations]);

  const loadOlderMessages = useCallback(async () => {
    if (!conversationId || !olderCursor || loadingOlderRef.current) {
      return;
    }
    const container = messagesContainerRef.current;
    const previousHeight = container?.scrollHeight ?? 0;
    loadingOlderRef.current = true;
    try {
      const page = await aiChatService.getMessages(conversationId, {
        before: olderCursor,
        limit: MESSAGE_PAGE_LIMIT,
      });
      const mapped = mapMessages(page.items || []);
      if (mapped.length > 0) {
        setMessages((prev) => [...mapped, ...prev]);
        window.requestAnimationFrame(() => {
          if (container) {
            const newHeight = container.scrollHeight;
            container.scrollTop = newHeight - previousHeight;
          }
        });
      }
      setOlderCursor(page.previousBefore ?? null);
    } catch (error) {
      console.error("Failed to load older messages", error);
    } finally {
      loadingOlderRef.current = false;
    }
  }, [conversationId, olderCursor, mapMessages]);


  useEffect(() => {
  fetchRateInfo();
  fetchConversations();
}, [fetchRateInfo, fetchConversations]);

// Toggle chat-open/chat-collapsed class on <body> for responsive ParallelAI grid
useEffect(() => {
  if (typeof document === 'undefined') return;
  if (renderVisibility === 'open') {
    document.body.classList.add('chat-open');
    document.body.classList.remove('chat-collapsed');
  } else {
    document.body.classList.add('chat-collapsed');
    document.body.classList.remove('chat-open');
  }
  return () => {
    document.body.classList.remove('chat-open');
    document.body.classList.remove('chat-collapsed');
  };
}, [renderVisibility]);

  useEffect(() => {
    if (conversationId) {
      if (skipNextConversationLoadRef.current) {
        skipNextConversationLoadRef.current = false;
        return;
      }
      loadConversationMessages(conversationId);
    } else {
      resetMessageAnimations();
      setMessages([]);
      setOlderCursor(null);
    }
  }, [conversationId, loadConversationMessages, resetMessageAnimations]);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    // Always run in agent mode; ignore any persisted "ask" preference.
    const storedMode = readStoredModes()[conversationId];
    if (storedMode === "agent") {
      setModeState("agent");
    }
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) {
      return;
    }
    const stored = readStoredModes();
    if (stored[conversationId] === mode) {
      return;
    }
    writeStoredModes({
      ...stored,
      [conversationId]: mode,
    });
  }, [conversationId, mode]);

  // Fetch available agents when in agent mode or picker is open
  useEffect(() => {
    if ((mode === "agent" || isAgentPickerOpen) && availableAgents.length === 0) {
      fetchAgentNames();
    }
  }, [availableAgents.length, fetchAgentNames, isAgentPickerOpen, mode]);

  useEffect(() => {
    if (isMobileMode || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(normalizedWidth));
  }, [normalizedWidth, isMobileMode]);

  useEffect(() => () => {
    if (isMobileMode || typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(normalizedWidth));
  }, [isMobileMode, normalizedWidth]);

  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
    };
  }, []);

  useEffect(() => () => {
    clearAnimationTimers();
    clearThinkingTimers();
    if (agentRefreshTimeoutRef.current) {
      window.clearTimeout(agentRefreshTimeoutRef.current);
      agentRefreshTimeoutRef.current = null;
    }
  }, [clearAnimationTimers, clearThinkingTimers]);

  useEffect(() => {
    if (!showThinkingBubble) {
      clearThinkingTimers();
      setThinkingDotsIndex(0);
      return;
    }

    setThinkingWord((previous) => pickRandomThinkingWord(thinkingWordPool, previous));

    const scheduleWordChange = () => {
      const delay = 2000 + Math.random() * 2000;
      thinkingWordTimeoutRef.current = window.setTimeout(() => {
        setThinkingWord((previous) => pickRandomThinkingWord(thinkingWordPool, previous));
        scheduleWordChange();
      }, delay);
    };

    scheduleWordChange();

    thinkingDotsIntervalRef.current = window.setInterval(() => {
      setThinkingDotsIndex((prev) => (prev + 1) % THINKING_DOTS.length);
    }, 500);

    return () => {
      clearThinkingTimers();
    };
  }, [clearThinkingTimers, showThinkingBubble, thinkingWordPool]);

  useEffect(() => {
    if (typeof window === "undefined") {
      setSessionKey(null);
      return;
    }
    setSessionKey(getChatSessionKey());
  }, [user?.userId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "refreshTokenExpiresAt" || event.key === "loggedInUserId" || event.key === "userId") {
        setSessionKey(getChatSessionKey());
      }
      if (event.key === AGENT_CONFIRM_STORAGE_KEY) {
        const nextValue = event.newValue ? Number(event.newValue) : 0;
        if (Number.isFinite(nextValue)) {
          setLastAgentConfirmAt(nextValue);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const toggleVisibility = () => {
    if (walkthroughActive) return;
    resizeCleanupRef.current?.();

    // Check if we're opening the chat while in multi-lot mode
    const willOpen = visibility === "closed" || (isMobileMode && !isMobileOpen);
    if (willOpen && isMultiLotMode) {
      setShowMultiLotWarning(true);
      return;
    }

    if (isMobileMode) {
      const next = isClosed ? "open" : "closed";
      onMobileVisibilityChange?.(next === "open");
      return;
    }
    setVisibility((prev) => {
      if (prev === "closed") {
        setChatWidth((current) => {
          const value = Number.isFinite(current) ? current : DEFAULT_OPEN_WIDTH;
          return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, value));
        });
        return "open";
      }
      return "closed";
    });
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStateChange = (event: Event) => {
      const detail = (event as CustomEvent<{ active: boolean }>).detail;
      const isActive = !!detail?.active;
      setWalkthroughActive(isActive);
      if (isActive) {
        setChatWidth((current) => {
          if (preWalkthroughWidthRef.current === null) {
            preWalkthroughWidthRef.current = current;
          }
          return current;
        });
        setVisibility((current) => {
          if (preWalkthroughVisibilityRef.current === null) {
            preWalkthroughVisibilityRef.current = current;
          }
          return current;
        });
      } else if (preWalkthroughWidthRef.current !== null) {
        setChatWidth(preWalkthroughWidthRef.current);
        preWalkthroughWidthRef.current = null;
        if (preWalkthroughVisibilityRef.current) {
          setVisibility(preWalkthroughVisibilityRef.current);
          preWalkthroughVisibilityRef.current = null;
        }
      }
    };

    const handleSetWidth = (event: Event) => {
      const detail = (event as CustomEvent<{ width: number | "max" | "restore" }>).detail;
      if (!detail) return;
      if (detail.width === "max") {
        setVisibility("open");
        setChatWidth(MAX_WIDTH);
      } else if (detail.width === "restore") {
        if (preWalkthroughWidthRef.current !== null) {
          setChatWidth(preWalkthroughWidthRef.current);
          preWalkthroughWidthRef.current = null;
        }
        if (preWalkthroughVisibilityRef.current) {
          setVisibility(preWalkthroughVisibilityRef.current);
          preWalkthroughVisibilityRef.current = null;
        }
      } else if (typeof detail.width === "number") {
        if (detail.width === 0) {
          setVisibility("closed");
        } else {
          setVisibility("open");
          setChatWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, detail.width)));
        }
      } else if (detail.width === "closed") {
        setVisibility("closed");
      }
    };

    const handleSetInput = (event: Event) => {
      const detail = (event as CustomEvent<{ value: string }>).detail;
      if (!detail) return;
      setInputValue(detail.value);
      window.requestAnimationFrame(() => {
        if (inputRef.current) {
          adjustComposerHeight(inputRef.current);
          inputRef.current.focus();
        }
      });
    };

    const handleSetMode = (event: Event) => {
      const detail = (event as CustomEvent<{ mode: ChatMode }>).detail;
      if (!detail) return;
      setModeState(detail.mode);
    };

    const handleSetTaggedAgents = (event: Event) => {
      const detail = (event as CustomEvent<{ agentKeys: string[] }>).detail;
      if (!detail?.agentKeys) return;
      // Store pending keys in a window-level ref; resolved in effect below
      (window as any).__walkthroughPendingAgentKeys = detail.agentKeys;
      // Clear existing immediately
      setTaggedAgents([]);
      autoTaggedAgentKeysRef.current = new Set();
      // Trigger re-resolution (bump a counter or dispatch a small event)
      window.dispatchEvent(new CustomEvent("walkthrough-resolve-agents"));
    };

    const handleClearTaggedAgents = () => {
      (window as any).__walkthroughPendingAgentKeys = null;
      setTaggedAgents([]);
      autoTaggedAgentKeysRef.current = new Set();
    };

    window.addEventListener(WALKTHROUGH_STATE_EVENT, handleStateChange as EventListener);
    window.addEventListener(CHAT_WALKTHROUGH_SET_WIDTH_EVENT, handleSetWidth as EventListener);
    window.addEventListener(CHAT_WALKTHROUGH_SET_INPUT_EVENT, handleSetInput as EventListener);
    window.addEventListener(CHAT_WALKTHROUGH_SET_MODE_EVENT, handleSetMode as EventListener);
    window.addEventListener(CHAT_WALKTHROUGH_SET_TAGGED_AGENTS_EVENT, handleSetTaggedAgents as EventListener);
    window.addEventListener(CHAT_WALKTHROUGH_CLEAR_TAGGED_AGENTS_EVENT, handleClearTaggedAgents as EventListener);

    return () => {
      window.removeEventListener(WALKTHROUGH_STATE_EVENT, handleStateChange as EventListener);
      window.removeEventListener(CHAT_WALKTHROUGH_SET_WIDTH_EVENT, handleSetWidth as EventListener);
      window.removeEventListener(CHAT_WALKTHROUGH_SET_INPUT_EVENT, handleSetInput as EventListener);
      window.removeEventListener(CHAT_WALKTHROUGH_SET_MODE_EVENT, handleSetMode as EventListener);
      window.removeEventListener(CHAT_WALKTHROUGH_SET_TAGGED_AGENTS_EVENT, handleSetTaggedAgents as EventListener);
      window.removeEventListener(CHAT_WALKTHROUGH_CLEAR_TAGGED_AGENTS_EVENT, handleClearTaggedAgents as EventListener);
    };
  }, []);

  // Handle confirming the multi-lot warning (clears to single lot and opens chat)
  const confirmMultiLotWarning = () => {
    clearToSingleLot();
    setShowMultiLotWarning(false);
    // Now open the chat
    if (isMobileMode) {
      onMobileVisibilityChange?.(true);
    } else {
      setVisibility("open");
      setChatWidth((current) => {
        const value = Number.isFinite(current) ? current : DEFAULT_OPEN_WIDTH;
        return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, value));
      });
    }
  };

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    messageEndRef.current?.scrollIntoView({ behavior });
  }, []);

  const scheduleScrollToBottom = useCallback(() => {
    window.requestAnimationFrame(() => {
      scrollToBottom();
    });
  }, [scrollToBottom]);

  useEffect(() => {
    if (inputRef.current) {
      adjustComposerHeight(inputRef.current);
    }
  }, [renderVisibility, mode, inputValue]);

  // Filter available agents for picker (exclude already-tagged agents)
  const filteredAgents = useMemo(() => {
    const term = agentSearchTerm.toLowerCase();
    return availableAgents.filter((agent) => {
      // Exclude already-tagged agents
      if (taggedAgents.some((t) => t.agentKey === agent.agentKey)) {
        return false;
      }
      // Filter by search term
      if (!term) return true;
      return (
        agent.name.toLowerCase().includes(term) ||
        agent.agentKey.toLowerCase().includes(term) ||
        (agent.description?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [availableAgents, taggedAgents, agentSearchTerm]);

  // Keep tagged agents in sync with mention text (add-only; chips remain even if text is cleared)
  useEffect(() => {
    if (mode !== "agent") {
      setTaggedAgents([]);
      autoTaggedAgentKeysRef.current = new Set();
      return;
    }

    if (!availableAgents.length) {
      return;
    }

    const matches: AiAgentNameItem[] = [];
    const mentionRegex = /@([^\s@]+)/g;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(inputValue)) !== null) {
      const mention = match[1].toLowerCase();
      const matchedAgent = availableAgents.find((agent) => {
        const keyMatch = agent.agentKey.toLowerCase() === mention;
        const nameMatch = (agent.name ?? "").toLowerCase() === mention;
        return keyMatch || nameMatch;
      });
      if (matchedAgent) {
        matches.push(matchedAgent);
      }
    }

    if (!matches.length) {
      return;
    }

    setTaggedAgents((prev) => {
      const existingKeys = new Set(prev.map((agent) => agent.agentKey));
      const additions = matches.filter((agent) => !existingKeys.has(agent.agentKey));
      if (!additions.length) {
        return prev;
      }
      return [...prev, ...additions];
    });
  }, [availableAgents, inputValue, mode]);

  // Handle agent selection from picker
  const handleAgentSelect = useCallback((agent: AiAgentNameItem) => {
    const buildSelection = (): { nextValue: string; caretPosition: number } => {
      if (atTriggerIndex !== null) {
        const before = inputValue.slice(0, atTriggerIndex);
        const afterAtAndSearch = inputValue.slice(atTriggerIndex);
        const spaceIndex = afterAtAndSearch.search(/\s/);
        const trailingText = spaceIndex >= 0 ? afterAtAndSearch.slice(spaceIndex).replace(/^\s*/, " ") : "";
        return {
          nextValue: `${before}${trailingText}`,
          caretPosition: before.length,
        };
      }
      const fallbackValue = inputValue.trimEnd();
      return {
        nextValue: fallbackValue,
        caretPosition: fallbackValue.length,
      };
    };

    const selection = buildSelection();

    if (mode === "ask" && !hasRecentAgentConsent()) {
      setPendingAgentSelection({
        agent,
        nextValue: selection.nextValue,
        caretPosition: selection.caretPosition,
      });
      pendingModeRef.current = "agent";
      setIsAgentConfirmOpen(true);
      setIsAgentPickerOpen(false);
      setAtTriggerIndex(null);
      setAgentSearchTerm("");
      return;
    }

    if (mode === "ask") {
      setModeState("agent");
    }

    applyAgentSelection({
      agent,
      nextValue: selection.nextValue,
      caretPosition: selection.caretPosition,
    });

    setIsAgentPickerOpen(false);
    setAtTriggerIndex(null);
    setAgentSearchTerm("");
  }, [applyAgentSelection, atTriggerIndex, hasRecentAgentConsent, inputValue, mode]);


  // Remove a tagged agent from the text
  const handleRemoveTaggedAgent = useCallback((agentKey: string) => {
    const escaped = agentKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`@${escaped}(?!\\w)`, "gi");
    setInputValue((prev) => {
      const next = prev.replace(regex, "").replace(/\s{2,}/g, " ").trim();
      window.requestAnimationFrame(() => {
        if (inputRef.current) {
          adjustComposerHeight(inputRef.current);
        }
      });
      return next;
    });
    setTaggedAgents((prev) => prev.filter((agent) => agent.agentKey !== agentKey));
    // Also remove from auto-tagged ref so it doesn't get re-added on route change
    autoTaggedAgentKeysRef.current.delete(agentKey);
  }, [adjustComposerHeight]);

  // Click outside handler for agent picker
  useEffect(() => {
    if (!isAgentPickerOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        agentPickerRef.current &&
        !agentPickerRef.current.contains(target) &&
        inputRef.current &&
        !inputRef.current.contains(target)
      ) {
        setIsAgentPickerOpen(false);
        setAtTriggerIndex(null);
        setAgentSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isAgentPickerOpen]);

  const finishAnimation = useCallback((messageId: string) => {
    setMessageAnimations((prev) => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });
    setMessages((prev) => prev.map((message) => (
      message.id === messageId ? { ...message, animate: false } : message
    )));
    animatingMessageRef.current = null;
    scheduleScrollToBottom();
  }, [scheduleScrollToBottom]);

  const startAnimationForMessage = useCallback((message: ChatMessage) => {
    clearAnimationTimers();
    animatingMessageRef.current = message.id;

    // Only the prose types in; tables/charts are shown as skeletons (handled in render), so we never
    // type out raw markdown.
    const animSource = getAnimatableProse(message.content);
    const tokens = animSource.match(/\S+\s*/g) ?? [];

    const buildSegments = (): string[] => {
      if (tokens.length === 0) {
        return [];
      }

      if (tokens.length === 1) {
        const token = tokens[0];
        if (token.trim().length <= 4) {
          return [token];
        }
        const mid = Math.ceil(token.length / 2);
        return [token.slice(0, mid), token.slice(mid)];
      }

      if (tokens.length <= 12) {
        return tokens;
      }

      const chunkSize = Math.max(1, Math.ceil(tokens.length / 12));
      const chunks: string[] = [];
      for (let index = 0; index < tokens.length; index += chunkSize) {
        chunks.push(tokens.slice(index, index + chunkSize).join(""));
      }
      return chunks;
    };

    const segments: string[] = buildSegments();

    if (segments.length === 0) {
      if (animSource.trim()) {
        segments.push(animSource);
      } else {
        // Block-only message (e.g. just a table/chart/card): let the skeleton breathe briefly, then
        // reveal the real content. No prose to type.
        animationTimeoutRef.current = window.setTimeout(() => {
          clearAnimationTimers();
          finishAnimation(message.id);
        }, 350);
        return;
      }
    }

    const totalDuration = animSource.length > 200 ? 800 : 400;

    const firstSegment = segments[0] ?? "";
    setMessageAnimations((prev) => ({
      ...prev,
      [message.id]: firstSegment,
    }));
    scheduleScrollToBottom();

    if (segments.length === 1) {
      animationTimeoutRef.current = window.setTimeout(() => {
        clearAnimationTimers();
        finishAnimation(message.id);
      }, totalDuration);
      return;
    }

    let revealedCount = 1;
    const steps = Math.max(segments.length - 1, 1);
    const intervalDuration = Math.max(totalDuration / steps, 45);

    animationIntervalRef.current = window.setInterval(() => {
      revealedCount += 1;
      setMessageAnimations((prev) => ({
        ...prev,
        [message.id]: segments.slice(0, Math.min(revealedCount, segments.length)).join(""),
      }));
      scheduleScrollToBottom();

      if (revealedCount >= segments.length) {
        clearAnimationTimers();
        finishAnimation(message.id);
      }
    }, intervalDuration);
  }, [clearAnimationTimers, finishAnimation, scheduleScrollToBottom]);

  const startAnimationIfNeeded = useCallback(() => {
    if (animatingMessageRef.current) {
      const messageStillAnimating = messages.some(
        (message) => message.id === animatingMessageRef.current && message.animate,
      );
      if (messageStillAnimating) {
        return;
      }
      animatingMessageRef.current = null;
    }

    const nextMessage = messages.find(
      (message) => message.role === "assistant" && message.animate,
    );

    if (!nextMessage) {
      setIsAnimatingResponse(false);
      return;
    }

    setIsAnimatingResponse(true);
    startAnimationForMessage(nextMessage);
  }, [messages, startAnimationForMessage]);

  useEffect(() => {
    startAnimationIfNeeded();
  }, [startAnimationIfNeeded]);

  const updateAgentTaskStatus = useCallback((taskId: string, status: string) => {
    if (!taskId) {
      return;
    }
    setMessages((prev) => prev.map((message) => {
      const eventNode = message.rawJson?.agent_event;
      const eventTaskId = eventNode?.task_id ?? eventNode?.taskId;
      if (!eventTaskId || eventTaskId !== taskId) {
        return message;
      }
      const updatedEvent = {
        ...eventNode,
        status,
      };
      const updatedJson = {
        ...message.rawJson,
        agent_event: updatedEvent,
      };
      return {
        ...message,
        rawJson: updatedJson,
      };
    }));
  }, []);

  // Clarifying-question picks, keyed by assistant message id then by question index → chosen option.
  // Drives the "answered" UI (picked option stays highlighted, the rest fade out and lock). When every
  // question in a card is answered, the combined answer is sent to the AI as one hidden message.
  const [clarifySelections, setClarifySelections] = useState<Record<string, Record<number, string>>>({});

  const handleAgentConfirm = useCallback(async (taskId: string) => {
    if (!taskId || confirmingTasks[taskId]) {
      return;
    }
    setConfirmingTasks((prev) => ({ ...prev, [taskId]: true }));
    try {
      const response = await aiChatService.confirmAgentAction(taskId);
      updateAgentTaskStatus(taskId, response.status ?? "applied");
      if (response.assistantMessage) {
        const assistant = response.assistantMessage;
        const newMessage: ChatMessage = {
          id: assistant.messageId,
          role: "assistant",
          content: extractMarkdown(assistant.content, assistant.contentJson),
          createdOn: assistant.createdOn,
          rawJson: assistant.contentJson,
        };
        setMessages((prev) => [...prev, newMessage]);
        scheduleScrollToBottom();
      }
      refreshPageAfterAgentConfirm();
    } catch (error) {
      console.error("Failed to confirm agent action", error);
      updateAgentTaskStatus(taskId, "error");
      const inWalkthrough =
        typeof document !== "undefined" &&
        document.body.classList.contains("walkthrough-active");
      if (inWalkthrough) {
        window.dispatchEvent(new CustomEvent("ai-chat-agent-error", { detail: { taskId } }));
      } else {
        const message = error instanceof Error && error.message
          ? error.message
          : "Unable to apply the agent action. Please try again.";
        window.alert(message);
      }
    } finally {
      setConfirmingTasks((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
    }
  }, [confirmingTasks, refreshPageAfterAgentConfirm, scheduleScrollToBottom, updateAgentTaskStatus]);

  const handleAgentCancel = useCallback(async (taskId: string) => {
    if (!taskId || cancellingTasks[taskId]) {
      return;
    }
    setCancellingTasks((prev) => ({ ...prev, [taskId]: true }));
    try {
      const response = await aiChatService.cancelAgentAction(taskId);
      updateAgentTaskStatus(taskId, response.status ?? "cancelled");
    } catch (error) {
      console.error("Failed to cancel agent action", error);
      const message = error instanceof Error && error.message
        ? error.message
        : "Unable to cancel the agent action. Please try again.";
      window.alert(message);
    } finally {
      setCancellingTasks((prev) => {
        const next = { ...prev };
        delete next[taskId];
        return next;
      });
    }
  }, [cancellingTasks, updateAgentTaskStatus]);

  const handleAgentView = useCallback((rawTarget?: string) => {
    let target = rawTarget;
    if (target && lotId) {
      // Resolve any leftover {lotId} placeholder.
      target = target.replace(/\{lotId\}/g, lotId);
      // Safety net: if the model emitted a bare section path (e.g. "/registry") without the lot,
      // expand it to the full lot-scoped route so the link doesn't 404.
      if (/^\/[a-zA-Z][a-zA-Z-]*\/?$/.test(target) && !target.startsWith("/lot/")) {
        target = `/lot/${lotId}/${target.replace(/^\/|\/$/g, "")}`;
      }
    }
    if (!target) {
      return;
    }
    if (/^https?:\/\//i.test(target)) {
      if (typeof window !== "undefined") {
        window.open(target, "_blank", "noopener");
      }
    } else {
      navigate(target);
    }
  }, [navigate, lotId]);

  // Records a clarifying-question pick. Locks that question; once all questions in the card are
  // answered, sends the combined answer to the AI as one hidden message.
  const handleClarifyPick = (
    messageId: string,
    questions: any[],
    qi: number,
    optionText: string,
  ) => {
    if (isSending) {
      return;
    }
    const existing = clarifySelections[messageId] ?? {};
    if (existing[qi] !== undefined) {
      return; // already answered this question
    }
    const nextForMessage = { ...existing, [qi]: optionText };
    setClarifySelections((prev) => ({
      ...prev,
      [messageId]: { ...(prev[messageId] ?? {}), [qi]: optionText },
    }));
    const allAnswered = questions.every((_, i) => nextForMessage[i] !== undefined);
    if (allAnswered) {
      void handleSend(undefined, buildClarifyAnswer(questions, nextForMessage), { hidden: true });
    }
  };

  // Clarifying-question card. Each question is answered independently via the UI; a picked option
  // locks that question (stays highlighted, siblings fade). Only once every question is answered do we
  // send a single combined answer to the AI as a hidden message (no user bubble). The answered card
  // persists even after newer messages arrive, so it stays clear what was chosen.
  const renderClarifyingQuestions = (message: ChatMessage, isLast: boolean) => {
    if (message.role !== "assistant") {
      return null;
    }
    const questions = message.rawJson?.clarifying_questions;
    if (!Array.isArray(questions) || questions.length === 0) {
      return null;
    }
    const selections = clarifySelections[message.id] ?? {};
    const hasAnySelection = Object.keys(selections).length > 0;
    // Show the card while it's the latest turn, or once the operator has started answering it.
    if (!isLast && !hasAnySelection) {
      return null;
    }
    return (
      <div className="ai-chat-clarify">
        {questions.map((q: any, qi: number) => {
          const answeredOption = selections[qi];
          const questionLocked = answeredOption !== undefined;
          return (
            <div key={`clarify-${qi}`} className="ai-chat-clarify-card">
              {q?.question && <p className="ai-chat-clarify-question">{q.question}</p>}
              <div className="ai-chat-clarify-options">
                {(Array.isArray(q?.options) ? q.options : []).map((opt: any, oi: number) => {
                  const optionText = String(opt);
                  const isSelected = questionLocked && answeredOption === optionText;
                  const isFaded = questionLocked && !isSelected;
                  return (
                    <button
                      key={`clarify-${qi}-opt-${oi}`}
                      type="button"
                      className={`ai-chat-clarify-option${isSelected ? " is-selected" : ""}${isFaded ? " is-faded" : ""}`}
                      disabled={isSending || questionLocked}
                      onClick={() => handleClarifyPick(message.id, questions, qi, optionText)}
                    >
                      {optionText}
                    </button>
                  );
                })}
              </div>
              {!questionLocked && q?.allow_other !== false && (
                <p className="ai-chat-clarify-other">Or type your own answer below.</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Data-access confirmation: the AI needs to read live lot data. Allow stores 24h consent and resends
  // the original question; Cancel dismisses. Only shown under the latest assistant message.
  const renderDataConfirm = (message: ChatMessage, isLast: boolean) => {
    if (!isLast || message.role !== "assistant") {
      return null;
    }
    const dataConfirm = message.rawJson?.data_confirm;
    if (!dataConfirm) {
      return null;
    }
    const original = typeof dataConfirm.original_message === "string" ? dataConfirm.original_message : "";
    return (
      <div className="ai-chat-dataconfirm">
        <p className="ai-chat-dataconfirm-text">
          Parallel AI needs access to view your lot&apos;s data to properly answer your questions.
        </p>
        <div className="ai-chat-dataconfirm-actions">
          <button
            type="button"
            className="ai-chat-dataconfirm-allow"
            disabled={isSending}
            onClick={() => {
              rememberDataConsent();
              if (original.trim()) {
                void handleSend(undefined, original);
              }
            }}
          >
            Allow
          </button>
          <button type="button" className="ai-chat-dataconfirm-cancel" disabled={isSending}>
            Not now
          </button>
        </div>
      </div>
    );
  };

  // Email draft: the AI composed an email for the operator to review and send themselves (draft only).
  const renderEmailDraft = (message: ChatMessage) => {
    if (message.role !== "assistant") {
      return null;
    }
    const draft = message.rawJson?.email_draft;
    if (!draft || (!draft.subject && !draft.body)) {
      return null;
    }
    const subject = typeof draft.subject === "string" ? draft.subject : "";
    const body = typeof draft.body === "string" ? draft.body : "";
    const to = typeof draft.to === "string" ? draft.to : "";
    return <EmailDraftCard key={message.id} to={to} subject={subject} body={body} />;
  };

  // Navigation suggestion: a button the AI surfaces to jump to a relevant app page.
  const renderNavigation = (message: ChatMessage) => {
    if (message.role !== "assistant") {
      return null;
    }
    const nav = message.rawJson?.navigation;
    const page = nav?.page;
    if (!nav || typeof page !== "string" || !page.trim()) {
      return null;
    }
    const target = page.trim();
    // Faded when we're already on the suggested page (mirrors the agent card's "View" button).
    const isCurrentPage = !/^https?:\/\//i.test(target) && (() => {
      try {
        const normalize = (path: string) => path.replace(/\/+$/, "") || "/";
        const targetUrl = new URL(target, window.location.origin);
        return normalize(targetUrl.pathname) === normalize(location.pathname);
      } catch {
        return target === location.pathname;
      }
    })();
    return (
      <div className="ai-chat-nav">
        <button
          type="button"
          className={`ai-chat-nav-button${isCurrentPage ? " is-current" : ""}`}
          disabled={isCurrentPage}
          onClick={() => handleAgentView(target)}
        >
          {typeof nav.label === "string" && nav.label.trim() ? nav.label : "Open page"}
        </button>
      </div>
    );
  };

  const handleMessageScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || !olderCursor || loadingOlderRef.current) {
      return;
    }
    if (container.scrollTop <= 40) {
      void loadOlderMessages();
    }
  }, [loadOlderMessages, olderCursor]);

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    const textarea = inputRef.current;
    if (textarea) {
      textarea.value = suggestion;
      adjustComposerHeight(textarea);
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  };

  // Process picked/dropped files: images become data URLs (sent to the vision model); spreadsheets/CSVs
  // are converted to text client-side and later appended to the message content.
  const processFiles = useCallback(async (files: File[]) => {
    for (const file of files) {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      try {
        if (file.type.startsWith("image/")) {
          const dataUrl = await readFileAsDataUrl(file);
          setAttachments((prev) => [...prev, { id, kind: "image", name: file.name, dataUrl }]);
        } else if (isSheetFile(file)) {
          const text = await sheetFileToText(file);
          setAttachments((prev) => [...prev, { id, kind: "sheet", name: file.name, text }]);
        } else {
          console.warn("Unsupported attachment type:", file.name, file.type);
        }
      } catch (error) {
        console.error("Failed to process attachment", file.name, error);
      }
    }
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0) {
      void processFiles(files);
    }
    event.target.value = ""; // allow re-selecting the same file
  }, [processFiles]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleComposerDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (files.length > 0) {
      void processFiles(files);
    }
  }, [processFiles]);

  const handleComposerDragOver = useCallback((event: React.DragEvent) => {
    if (event.dataTransfer?.types?.includes("Files")) {
      event.preventDefault();
      setIsDragging(true);
    }
  }, []);

  const handleComposerDragLeave = useCallback((event: React.DragEvent) => {
    if (event.currentTarget === event.target) {
      setIsDragging(false);
    }
  }, []);

  const handleResizeMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isMobileMode || isClosed) {
      return;
    }
    if (walkthroughActive) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    resizeCleanupRef.current?.();
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = chatWidth;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";

    let cleanup = () => {};

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      let nextWidth = startWidth + delta;

      if (nextWidth < SNAP_CLOSE_THRESHOLD) {
        cleanup();
        setVisibility("closed");
        return;
      }

      const currentMax = maxWidth !== undefined ? Math.min(MAX_WIDTH, maxWidth) : MAX_WIDTH;
      nextWidth = Math.max(MIN_WIDTH, Math.min(currentMax, nextWidth));
      setChatWidth(nextWidth);
    };

    const handleMouseUp = () => {
      cleanup();
    };

    cleanup = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = previousUserSelect;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      resizeCleanupRef.current = null;
    };

    resizeCleanupRef.current = cleanup;

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const startConversation = useCallback(async (initialTitle: string) => {
    if (!lotId) {
      throw new Error("Lot is required to start a conversation");
    }
    const conversation = await aiChatService.startConversation(lotId, initialTitle.slice(0, 60));
    setConversations((prev) => [
      conversation,
      ...prev.filter((existing) => existing.conversationId !== conversation.conversationId),
    ]);
    return conversation.conversationId;
  }, [lotId]);

  const handleNewChat = useCallback(() => {
    setConversationId(null);
    resetMessageAnimations();
    setMessages([]);
    setOlderCursor(null);
    skipNextConversationLoadRef.current = false;
  }, [resetMessageAnimations]);

  useEffect(() => {
    if (!lotId) {
      sessionInfoRef.current = { sessionKey: null, lotId: null };
      handleNewChat();
      return;
    }
    if (!sessionKey) {
      sessionInfoRef.current = { sessionKey: null, lotId };
      handleNewChat();
      return;
    }
    const hasSessionChanged = sessionInfoRef.current.sessionKey !== sessionKey;
    const hasLotChanged = sessionInfoRef.current.lotId !== lotId;
    if (!hasSessionChanged && !hasLotChanged) {
      return;
    }
    sessionInfoRef.current = { sessionKey, lotId };
    const storedConversation = readStoredConversationId(sessionKey, lotId);
    if (storedConversation) {
      setConversationId(storedConversation);
    } else {
      handleNewChat();
    }
  }, [handleNewChat, lotId, sessionKey]);

  useEffect(() => {
    if (!sessionKey || !lotId) {
      return;
    }
    persistStoredConversationId(sessionKey, lotId, conversationId);
  }, [conversationId, lotId, sessionKey]);

  const handleSend = async (
    event?: React.FormEvent,
    overrideText?: string,
    options?: { hidden?: boolean },
  ) => {
    event?.preventDefault();
    const hasOverride = typeof overrideText === "string" && overrideText.trim().length > 0;
    // Override sends (e.g. clarifying-question answers) bypass the composer's canSend check but still
    // respect in-flight + rate-limit guards.
    if (hasOverride) {
      if (isSending || !lotId || (remainingUses !== null && remainingUses <= 0)) return;
    } else if (!canSend) {
      return;
    }

    const trimmed = (hasOverride ? overrideText : inputValue).trim();
    const agentKeysToSend = taggedAgents.map((a) => a.agentKey);

    // Attachments only ride along on normal sends (not clarifying-answer overrides).
    const sendAttachments = hasOverride ? [] : attachments;
    const sheetText = sendAttachments
      .filter((a) => a.kind === "sheet")
      .map((a) => `\n\n[Attached spreadsheet: ${a.name}]\n${a.text ?? ""}`)
      .join("");
    const imagesToSend = sendAttachments
      .filter((a) => a.kind === "image" && a.dataUrl)
      .map((a) => a.dataUrl as string);
    const attachmentNames = sendAttachments.map((a) => a.name);
    // The model gets the spreadsheet text appended; the visible bubble stays clean (attachment names
    // render separately with the paperclip icon).
    const baseContent = `${trimmed}${sheetText}`;
    const contentToSend = baseContent.trim().length > 0 ? baseContent : "Please review the attached file(s).";

    setInputValue("");
    setTaggedAgents([]); // Clear tagged agents after sending
    setAttachments([]); // Clear attachments after sending
    autoTaggedAgentKeysRef.current = new Set(); // Clear auto-tagged tracking after sending
    setIsSending(true);
    if (inputRef.current) {
      inputRef.current.value = "";
      adjustComposerHeight(inputRef.current);
    }

    // Create AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const tempId = buildTempId();
    pendingUserMessageIdRef.current = tempId;
    const userBubble: ChatMessage = {
      id: tempId,
      role: "user",
      content: trimmed,
      pending: true,
      animate: false,
      rawJson: null,
      hiddenFromChat: options?.hidden === true,
      attachmentItems: sendAttachments.length > 0
        ? sendAttachments.map((a) => ({ name: a.name, kind: a.kind }))
        : undefined,
    };
    setMessages((prev) => [...prev, userBubble]);
    scheduleScrollToBottom();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_SENT_EVENT, {
        detail: { content: trimmed, mode },
      }));
    }

    try {
      let activeConversationId = conversationId;
      let newlyCreatedConversationId: string | null = null;
      if (!activeConversationId) {
        activeConversationId = await startConversation(trimmed || attachmentNames[0] || "New chat");
        newlyCreatedConversationId = activeConversationId;
        // Track the new conversation ID for cancel requests
        pendingConversationIdRef.current = activeConversationId;
      }

      const payload = await aiChatService.sendMessage({
        conversationId: activeConversationId,
        lotId,
        content: contentToSend,
        mode,
        taggedAgentKeys: agentKeysToSend.length > 0 ? agentKeysToSend : undefined,
        dataConsent: hasRecentDataConsent(),
        images: imagesToSend.length > 0 ? imagesToSend : undefined,
      }, abortController.signal);

      // Guard against state updates if component unmounted during async operation
      if (!isMountedRef.current) return;

      setMessages((prev) => prev.map((message) => (
        message.id === tempId
          ? { ...message, id: payload.messageId, pending: false }
          : message
      )));

      const assistantList = payload.assistantMessages && payload.assistantMessages.length > 0
        ? payload.assistantMessages
        : (payload.assistantMessage ? [payload.assistantMessage] : []);

      assistantList.forEach((msg) => {
        // Check mount status before each state update in the loop
        if (!isMountedRef.current) return;

        const assistantMessage: ChatMessage = {
          id: msg.messageId,
          role: "assistant",
          content: extractMarkdown(msg.content, msg.contentJson),
          createdOn: msg.createdOn,
          animate: true,
          rawJson: msg.contentJson,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        setMessageAnimations((prev) => ({
          ...prev,
          [assistantMessage.id]: "",
        }));
        setIsAnimatingResponse(true);
        scheduleScrollToBottom();
      });

      if (!isMountedRef.current) return;

      if (payload.rate) {
        setRateInfo(payload.rate);
      } else {
        fetchRateInfo();
      }
      fetchConversations();
      if (newlyCreatedConversationId) {
        skipNextConversationLoadRef.current = true;
        setConversationId(newlyCreatedConversationId);
        setOlderCursor(null);
      }
    } catch (error) {
      // Guard against state updates if component unmounted
      if (!isMountedRef.current) return;

      // Check if this was a cancellation
      const isCancelled = error instanceof Error && error.name === "CanceledError";
      if (isCancelled) {
        // Keep the user message but mark as not pending, and add "Stopped" response
        setMessages((prev) => prev.map((message) => (
          message.id === tempId
            ? { ...message, pending: false }
            : message
        )));
        setMessages((prev) => [
          ...prev,
          {
            id: buildTempId(),
            role: "assistant",
            content: "Stopped",
            animate: false,
            rawJson: null,
          },
        ]);
      } else {
        console.error("Failed to send AI message", error);
        setMessages((prev) => prev.filter((message) => message.id !== tempId));
        setMessages((prev) => [
          ...prev,
          {
            id: buildTempId(),
            role: "assistant",
            content: "Parallel AI couldn't send that. Please try again.",
            animate: false,
            rawJson: null,
          },
        ]);
      }
      scheduleScrollToBottom();
      setIsAnimatingResponse(false);
    } finally {
      setIsSending(false);
      abortControllerRef.current = null;
      pendingUserMessageIdRef.current = null;
      pendingConversationIdRef.current = null;
      scheduleScrollToBottom();
    }
  };

  const handleCancelRequest = useCallback(async () => {
    // First, abort the frontend request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Use the pending conversation ID if available (for new conversations),
    // otherwise use the current conversation ID
    const targetConversationId = pendingConversationIdRef.current || conversationId;

    // Then, notify the backend to cancel if we have an active conversation
    if (targetConversationId) {
      try {
        await aiChatService.cancelRequest(targetConversationId);
      } catch (error) {
        // Ignore cancel request errors - the abort already handled the frontend
        console.debug("Cancel request to backend failed (may already be complete)", error);
      }
    }
  }, [conversationId]);

  const handleConversationSelect = useCallback((id: string) => {
    if (id === conversationId) return;
    setConversationId(id);
  }, [conversationId]);

  const historyMenuItems: MenuItem[] = useMemo(() => {
    if (isLoadingHistory && historyMenuOpen) {
      return [{
        key: "loading",
        label: "Loading conversations...",
        disabled: true,
        closeOnSelect: false,
      }];
    }
    if (conversations.length === 0) {
      return [{
        key: "empty",
        label: "No conversations yet",
        disabled: true,
        closeOnSelect: false,
      }];
    }
    return conversations.map((conversation) => ({
      key: conversation.conversationId,
      label: (conversation.title?.trim() || "Untitled conversation"),
      selected: conversation.conversationId === conversationId,
      onSelect: () => handleConversationSelect(conversation.conversationId),
    }));
  }, [conversations, conversationId, handleConversationSelect, historyMenuOpen, isLoadingHistory]);

  const handleYoutubeOpen = useCallback((url: string) => {
    setVideoModal({ isOpen: true, url, title: "YouTube Video" });
  }, []);

  const handleVideoClose = useCallback(() => {
    setVideoModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const renderAgentEvent = useCallback((event: any) => {
    if (!event || typeof event !== "object") {
      return null;
    }
    const eventType = (event.type ?? "").toLowerCase();
    const taskId: string | undefined = event.task_id ?? event.taskId;
    const status = (event.status ?? "pending").toLowerCase();
    const title = event.agent_name ?? event.agent_key ?? "Agent action";
    const confirmLabel = event.confirm_label ?? event.actions?.confirm_label ?? "Confirm";
    const isConfirming = Boolean(taskId && confirmingTasks[taskId]);
    const isCancelling = Boolean(taskId && cancellingTasks[taskId]);
    const canConfirm = status === "pending" && taskId;
    const canCancel = status === "pending" && taskId;
    const rawViewTarget = event.view_url
      ?? event.viewUrl
      ?? event.actions?.view_url
      ?? event.actions?.viewUrl
      ?? event.actions?.view?.url
      ?? event.actions?.view?.href
      ?? event.actions?.view;
    const viewTarget = typeof rawViewTarget === "string"
      ? rawViewTarget
      : (rawViewTarget && typeof rawViewTarget === "object"
        ? (rawViewTarget.url ?? rawViewTarget.href ?? null)
        : null);
    const viewLabel = event.view_label ?? event.actions?.view_label ?? "View";
    const canView = Boolean(viewTarget) && eventType === "agent_action_result";
    const isCurrentViewTarget = (() => {
      if (!viewTarget || /^https?:\/\//i.test(viewTarget)) {
        return false;
      }
      try {
        const normalize = (path: string) => path.replace(/\/+$/, "") || "/";
        const targetUrl = new URL(viewTarget, window.location.origin);
        return normalize(targetUrl.pathname) === normalize(location.pathname);
      } catch {
        return viewTarget === location.pathname;
      }
    })();
    // The advanced-pricing agent gets a custom, day-grouped diff preview instead of the generic table.
    const advKey = (event.agent_key ?? "").toLowerCase();
    const advName = (event.agent_name ?? "").toLowerCase();
    const looksAdvancedPricing = (advKey.includes("advanced") && advKey.includes("pricing"))
      || (advName.includes("advanced") && advName.includes("pricing"));
    const isAdvancedPricing = looksAdvancedPricing
      && (event.preview?.type ?? "").toLowerCase() === "table"
      && Array.isArray(event.preview?.rows);
    const previewNode = isAdvancedPricing
      ? <AdvancedPricingPreview preview={event.preview} />
      : renderAgentPreviewContent(event.preview);
    const showActions = canConfirm || canCancel;

    const getStatusLabel = (s: string) => {
      switch (s) {
        case "pending": return "Pending";
        case "cancelled": return "Cancelled";
        case "applied": return "Applied";
        case "error": return "Error";
        case "expired": return "Expired";
        default: return s.charAt(0).toUpperCase() + s.slice(1);
      }
    };
    const statusLabel = getStatusLabel(status);

    return (
      <div className="ai-chat-agent-card">
        <div className="ai-chat-agent-header">
          <span className="ai-chat-agent-title">{title}</span>
          <div className="ai-chat-agent-status-group">
            <span className={`ai-chat-agent-status is-${status}`}>{statusLabel}</span>
            {canView && (
              <button
                type="button"
                className={`ai-chat-agent-view${isCurrentViewTarget ? " is-current" : ""}`}
                onClick={() => handleAgentView(viewTarget!)}
              >
                {viewLabel}
              </button>
            )}
          </div>
        </div>
        {event.summary && (
          <p className="ai-chat-agent-summary">{event.summary}</p>
        )}
        {previewNode}
        {showActions && (
          <div className="ai-chat-agent-actions">
            {canConfirm && (
              <button
                type="button"
                className="ai-chat-agent-confirm"
                disabled={isConfirming || isCancelling}
                onClick={() => handleAgentConfirm(taskId!)}
              >
                <span
                  className="ai-chat-agent-action-icon"
                  aria-hidden="true"
                  style={{
                    WebkitMaskImage: "url(/assets/ai/check.svg)",
                    maskImage: "url(/assets/ai/check.svg)",
                  }}
                />
                {isConfirming ? "Applying..." : confirmLabel}
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                className="ai-chat-agent-cancel"
                disabled={isConfirming || isCancelling}
                onClick={() => handleAgentCancel(taskId!)}
              >
                <span
                  className="ai-chat-agent-action-icon"
                  aria-hidden="true"
                  style={{
                    WebkitMaskImage: "url(/assets/ai/x.svg)",
                    maskImage: "url(/assets/ai/x.svg)",
                  }}
                />
                {isCancelling ? "Cancelling..." : "Cancel"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }, [confirmingTasks, cancellingTasks, handleAgentConfirm, handleAgentCancel, handleAgentView, location.pathname]);

  const suggestionPool = useMemo(() => {
    const shuffled = (mode === "agent" ? AGENT_MODE_SUGGESTIONS : ALL_SUGGESTIONS)
      .map((value) => ({ value, sortKey: Math.random() }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map((entry) => entry.value);
    return shuffled.slice(0, 3);
  }, [conversationId, mode]);

  const shouldShowSuggestions = messages.length === 0 && !conversationId && !isLoadingMessages;
  const mentionHighlight = useMemo(() => {
    if (atTriggerIndex === null) {
      return null;
    }
    const slice = inputValue.slice(atTriggerIndex);
    const match = slice.match(/^@[^\s@]*/);
    if (!match) {
      return null;
    }
    const mentionText = match[0];
    const before = inputValue.slice(0, atTriggerIndex);
    const after = inputValue.slice(atTriggerIndex + mentionText.length);
    const searchTerm = mentionText.slice(1);
    const hasSearch = searchTerm.length > 0;
    const hasResults = filteredAgents.length > 0;
    return {
      before,
      mentionText,
      after: after || "\u200b",
      hasSearch,
      hasResults,
    };
  }, [atTriggerIndex, filteredAgents.length, inputValue]);
  const hasMentionHighlight = Boolean(mentionHighlight);

  return (
    <>
      <aside
        className={`ai-chat ${renderVisibility === "open" ? "ai-chat--open" : "ai-chat--closed"}${isCompact ? " ai-chat--compact" : ""}${isMobileMode ? " ai-chat--mobile" : ""}`}
        aria-expanded={!isClosed}
        style={chatInlineStyle}
      >
      {!isClosed && !isMobileMode && (
        <div
          className="ai-chat-resize-handle"
          onMouseDown={handleResizeMouseDown}
          role="presentation"
        >
          <span className="ai-chat-resize-line" />
        </div>
      )}
      <div className="ai-chat-toolbar">
        <button
          type="button"
          className="ai-chat-icon-button"
          aria-label={isClosed ? "Open chat" : "Hide chat"}
          onClick={toggleVisibility}
        >
          <img src={sizeIcon} alt="" aria-hidden="true" />
        </button>
        {!isClosed && (
          <div className="ai-chat-toolbar-right">
            <Menu
              trigger={(
                <button
                  type="button"
                  className={`ai-chat-icon-button ai-chat-history-button${historyMenuOpen ? " is-active" : ""}`}
                  aria-pressed={historyMenuOpen}
                  aria-label="Conversation history"
                  disabled={!lotId}
                >
                  <img src="/assets/ai/history.svg" alt="" aria-hidden="true" />
                </button>
              )}
              header={{
                title: "New Chat",
                titleIcon: <img src="/assets/ai/new_chat.svg" alt="" aria-hidden="true" />,
                onTitleClick: handleNewChat,
              }}
              items={historyMenuItems}
              placement="bottom-end"
              dropdownClassName="ai-chat-history-menu"
              matchTriggerWidth={false}
              onOpenChange={(open) => {
                setHistoryMenuOpen(open);
                if (open) {
                  fetchConversations();
                }
              }}
            />
          </div>
        )}
      </div>

      {!isClosed && (
        <>
          <div className="ai-chat-body">
            <div
              className="ai-chat-messages"
              role="log"
              aria-live="polite"
              ref={messagesContainerRef}
              onScroll={handleMessageScroll}
              style={shouldShowSuggestions ? { display: "none" } : undefined}
            >
              {!shouldShowSuggestions && (
                <>
                  {messages.map((message, messageIndex) => {
                    const animatedText = messageAnimations[message.id] ?? "";
                    const shouldAnimate = Boolean(message.animate);
                    const agentEvent = message.rawJson?.agent_event;
                    const isLastMessage = messageIndex === messages.length - 1;
                    // Hidden answers (e.g. clarifying-question picks) are sent but never shown as a bubble.
                    if (message.hiddenFromChat) {
                      return null;
                    }
                    return (
                      <div
                        key={message.id}
                        className={`ai-chat-message ai-chat-message--${message.role}${message.pending ? " ai-chat-message--pending" : ""}`}
                      >
                        <div className="ai-chat-message-content">
                          {shouldAnimate ? (
                            <>
                              {animatedText && (
                                <p className="ai-chat-message-typing">{animatedText}</p>
                              )}
                              <MessageBlockSkeletons message={message} />
                            </>
                          ) : (
                            <>
                              {message.attachmentItems && message.attachmentItems.length > 0 && (
                                <div className="ai-chat-message-attachments">
                                  {message.attachmentItems.map((att, attIndex) => {
                                    const iconUrl = att.kind === "image"
                                      ? "url(/assets/ai/image.svg)"
                                      : "url(/assets/ai/table.svg)";
                                    return (
                                      <span key={`att-${attIndex}`} className="ai-chat-message-attachment">
                                        <span
                                          className="ai-chat-message-attachments-icon"
                                          aria-hidden="true"
                                          style={{ WebkitMaskImage: iconUrl, maskImage: iconUrl } as React.CSSProperties}
                                        />
                                        <span>{att.name}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              {/* For the active data-consent prompt the card supplies its own copy, so
                                  skip the model's redundant message body. */}
                              {!(message.rawJson?.data_confirm && isLastMessage)
                                && renderMarkdownContent(message.content, handleYoutubeOpen)}
                              {renderAgentEvent(agentEvent)}
                              {renderEmailDraft(message)}
                              {renderNavigation(message)}
                              {renderClarifyingQuestions(message, isLastMessage)}
                              {renderDataConfirm(message, isLastMessage)}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {isLoadingMessages && (
                    <div className="ai-chat-message ai-chat-message--assistant">
                      <span className="ai-chat-thinking-text">Loading conversation...</span>
                    </div>
                  )}
                  {showThinkingBubble && (
                    <div className="ai-chat-message ai-chat-message--assistant">
                      <div className="ai-chat-thinking-bubble">
                        <svg
                          className="ai-chat-thinking-spark"
                          viewBox="0 0 347 358"
                          aria-hidden="true"
                        >
                          <path
                            className="ai-chat-thinking-spark-big"
                            d="M142.227 54.9343C142.903 51.3169 144.822 48.0498 147.653 45.6987C150.484 43.3476 154.048 42.0605 157.728 42.0605C161.408 42.0605 164.972 43.3476 167.803 45.6987C170.634 48.0498 172.554 51.3169 173.229 54.9343L189.802 142.579C190.98 148.81 194.008 154.541 198.492 159.025C202.976 163.509 208.707 166.537 214.938 167.715L302.583 184.288C306.2 184.963 309.467 186.883 311.818 189.714C314.169 192.545 315.456 196.109 315.456 199.789C315.456 203.469 314.169 207.033 311.818 209.864C309.467 212.695 306.2 214.614 302.583 215.29L214.938 231.863C208.707 233.04 202.976 236.068 198.492 240.552C194.008 245.036 190.98 250.768 189.802 256.999L173.229 344.643C172.554 348.261 170.634 351.528 167.803 353.879C164.972 356.23 161.408 357.517 157.728 357.517C154.048 357.517 150.484 356.23 147.653 353.879C144.822 351.528 142.903 348.261 142.227 344.643L125.654 256.999C124.477 250.768 121.449 245.036 116.965 240.552C112.481 236.068 106.749 233.04 100.518 231.863L12.8738 215.29C9.25639 214.614 5.98924 212.695 3.63812 209.864C1.28701 207.033 0 203.469 0 199.789C0 196.109 1.28701 192.545 3.63812 189.714C5.98924 186.883 9.25639 184.963 12.8738 184.288L100.518 167.715C106.749 166.537 112.481 163.509 116.965 159.025C121.449 154.541 124.477 148.81 125.654 142.579L142.227 54.9343Z"
                          />
                          <path
                            className="ai-chat-thinking-spark-small"
                            d="M266.158 6.00776C266.473 4.31965 267.369 2.79498 268.69 1.69779C270.011 0.600603 271.674 0 273.392 0C275.109 0 276.772 0.600603 278.093 1.69779C279.414 2.79498 280.31 4.31965 280.625 6.00776L288.36 46.9085C288.909 49.8163 290.322 52.491 292.415 54.5836C294.507 56.6761 297.182 58.0892 300.09 58.6385L340.99 66.3727C342.679 66.688 344.203 67.5838 345.3 68.9049C346.398 70.226 346.998 71.8892 346.998 73.6065C346.998 75.3238 346.398 76.987 345.3 78.3081C344.203 79.6292 342.679 80.525 340.99 80.8403L300.09 88.5745C297.182 89.1238 294.507 90.5369 292.415 92.6295C290.322 94.722 288.909 97.3967 288.36 100.305L280.625 141.205C280.31 142.893 279.414 144.418 278.093 145.515C276.772 146.612 275.109 147.213 273.392 147.213C271.674 147.213 270.011 146.612 268.69 145.515C267.369 144.418 266.473 142.893 266.158 141.205L258.424 100.305C257.874 97.3967 256.461 94.722 254.369 92.6295C252.276 90.5369 249.601 89.1238 246.694 88.5745L205.793 80.8403C204.105 80.525 202.58 79.6292 201.483 78.3081C200.386 76.987 199.785 75.3238 199.785 73.6065C199.785 71.8892 200.386 70.226 201.483 68.9049C202.58 67.5838 204.105 66.688 205.793 66.3727L246.694 58.6385C249.601 58.0892 252.276 56.6761 254.369 54.5836C256.461 52.491 257.874 49.8163 258.424 46.9085L266.158 6.00776Z"
                          />
                        </svg>
                        <span className="ai-chat-thinking-label">
                          {thinkingWord}
                          {thinkingDots}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={messageEndRef} />
            </div>

            {shouldShowSuggestions && (
              <div className="ai-chat-suggestions" aria-label="Suggested prompts">
                <p className="ai-chat-greeting">Hi {greetingName}, how can I help you?</p>
                <div className="ai-chat-suggestion-list">
                  {suggestionPool.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      className="ai-chat-suggestion"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <form
            className={`ai-chat-composer${isDragging ? " is-dragging" : ""}`}
            onSubmit={handleSend}
            onDragOver={handleComposerDragOver}
            onDragLeave={handleComposerDragLeave}
            onDrop={handleComposerDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.csv,.xls,.xlsx"
              multiple
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />
            {isDragging && (
              <div className="ai-chat-drop-overlay">Drag and Drop</div>
            )}
            {/* Tagged agents + attachments (inline, same row) */}
            {(taggedAgents.length > 0 || attachments.length > 0) && (
              <div className="ai-chat-tagged-agents">
                {taggedAgents.map((agent) => (
                  <span key={agent.agentKey} className="ai-chat-agent-tag">
                    <span className="ai-chat-agent-tag-name">{agent.name}</span>
                    <button
                      type="button"
                      className="ai-chat-agent-tag-remove"
                      onClick={() => handleRemoveTaggedAgent(agent.agentKey)}
                      aria-label={`Remove ${agent.name}`}
                    >
                      <svg viewBox="0 0 12 12" width="10" height="10">
                        <path
                          d="M3 3l6 6M9 3l-6 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
                {attachments.map((attachment) => (
                  <span key={attachment.id} className="ai-chat-agent-tag ai-chat-attachment-tag">
                    <span
                      className="ai-chat-attachment-tag-icon"
                      aria-hidden="true"
                      style={(() => {
                        const url = `url(/assets/ai/${attachment.kind === "image"
                          ? "image"
                          : attachment.kind === "sheet"
                            ? "table"
                            : "other"}.svg)`;
                        return { WebkitMaskImage: url, maskImage: url } as React.CSSProperties;
                      })()}
                    />
                    <span className="ai-chat-agent-tag-name">{attachment.name}</span>
                    <button
                      type="button"
                      className="ai-chat-agent-tag-remove ai-chat-attachment-tag-remove"
                      onClick={() => removeAttachment(attachment.id)}
                      aria-label={`Remove ${attachment.name}`}
                    >
                      <svg viewBox="0 0 12 12" width="10" height="10">
                        <path
                          d="M3 3l6 6M9 3l-6 6"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Agent picker dropdown */}
            {isAgentPickerOpen && (
              <div ref={agentPickerRef} className="ai-chat-agent-picker">
                <div className="ai-chat-agent-picker-list">
                  {filteredAgents.length === 0 ? (
                    <div className="ai-chat-agent-picker-empty">No agents found</div>
                  ) : (
                    filteredAgents.map((agent) => (
                      <button
                        key={agent.agentKey}
                        type="button"
                        className="ai-chat-agent-picker-item"
                        onClick={() => handleAgentSelect(agent)}
                      >
                        <span className="ai-chat-agent-picker-name">{agent.name}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="ai-chat-input-wrapper">
              <div className={`ai-chat-input-stack${hasMentionHighlight ? " has-highlight" : ""}`}>
                {hasMentionHighlight && mentionHighlight && (
                  <div className="ai-chat-input-overlay" aria-hidden="true">
                    <pre>
                      {mentionHighlight.before}
                      <span
                        className={`ai-chat-mention${
                          mentionHighlight.hasResults
                            ? " is-searching"
                            : (mentionHighlight.hasSearch ? " is-unmatched" : "")
                        }`}
                      >
                        {mentionHighlight.mentionText || "@"}
                      </span>
                      {mentionHighlight.after}
                    </pre>
                  </div>
                )}
                <textarea
                  ref={inputRef}
                  className={`ai-chat-input${hasMentionHighlight ? " ai-chat-input--overlayed" : ""}`}
                  rows={1}
                  value={inputValue}
                  onChange={(event) => {
                    const value = event.target.value;
                    const cursorPos = event.target.selectionStart ?? 0;
                    setInputValue(value);
                    adjustComposerHeight(event.target);

                    // @ mention detection
                    const textBeforeCursor = value.slice(0, cursorPos);
                    const lastAtIndex = textBeforeCursor.lastIndexOf("@");

                    if (lastAtIndex >= 0) {
                      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : " ";
                      const isWordBoundary = /\s/.test(charBeforeAt) || lastAtIndex === 0;
                      const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
                      const hasNoSpaceAfterAt = !/\s/.test(textAfterAt);

                      if (isWordBoundary && hasNoSpaceAfterAt) {
                        setAgentSearchTerm(textAfterAt);
                        setAtTriggerIndex(lastAtIndex);
                        setIsAgentPickerOpen(true);
                        return;
                      }
                    }

                    setIsAgentPickerOpen(false);
                    setAtTriggerIndex(null);
                  }}
                  onKeyDown={(event) => {
                    // Escape closes agent picker
        if (event.key === "Escape" && isAgentPickerOpen) {
          event.preventDefault();
          setIsAgentPickerOpen(false);
          setAtTriggerIndex(null);
          setAgentSearchTerm("");
          return;
                    }

                    if (event.key === "Enter" && !event.shiftKey) {
                      if (isAgentPickerOpen || atTriggerIndex !== null) {
                        event.preventDefault();
                        if (filteredAgents.length > 0) {
                          handleAgentSelect(filteredAgents[0]);
                        } else {
                          setIsAgentPickerOpen(false);
                          setAtTriggerIndex(null);
                        }
                        return;
                      }
                      event.preventDefault();
                      if (canSend) {
                        handleSend();
                      }
                    }
                  }}
                  placeholder="What can I do? @mention agents"
                  aria-label="Message Parallel"
                />
              </div>
            </div>
            <div className="ai-chat-footer">
              <div className="ai-chat-footer-left">
                <button
                  type="button"
                  className="ai-chat-attach-trigger"
                  aria-label="Add image or spreadsheet"
                  onClick={handleAttachClick}
                >
                  <img src="/assets/ai/plus.svg" alt="" aria-hidden="true" />
                </button>
                {showRateLimit && (
                  <div className="ai-chat-usage">
                    <span className="ai-chat-usage-value">
                      <span className="ai-chat-usage-count">
                        {remainingUses ?? "—"}/{rateInfo?.quota ?? "—"}
                      </span>
                    </span>
                    <Tooltip
                      text={
                        rateInfo?.resetAt
                          ? `Daily limit resets at ${formatTimestamp(rateInfo.resetAt)}`
                          : "Daily limit resets every 24 hours."
                      }
                      size="small"
                      position="left"
                    >
                      <span className="ai-chat-usage-info" aria-hidden="true">
                        ?
                      </span>
                    </Tooltip>
                  </div>
                )}
              </div>
              {isSending ? (
                <button
                  type="button"
                  className="ai-chat-send-button is-sending is-stop"
                  onClick={handleCancelRequest}
                  aria-label="Stop generating"
                >
                  <svg
                    className="ai-chat-send-icon ai-chat-send-icon--progress"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <rect x="6" y="6" width="8" height="8" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  className={`ai-chat-send-button${canSend ? " is-ready" : ""}`}
                  disabled={!canSend}
                  aria-disabled={!canSend}
                  aria-label="Send message"
                >
                  <img
                    src="/assets/ai/arrow-up.svg"
                    className="ai-chat-send-icon"
                    alt=""
                    aria-hidden="true"
                  />
                </button>
              )}
            </div>
          </form>
        </>
      )}
      </aside>
      <Modal
        isOpen={isAgentConfirmOpen}
        title="Switch to Agent Mode"
        confirmText="Use Agent"
        cancelText="Cancel"
        onConfirm={handleAgentModeConfirm}
        onCancel={handleAgentModeCancel}
      >
        <div className="ai-chat-agent-confirm-content">
          <p>
            Parallel Agent is in beta and can make account changes for you, including updating lot settings,
            modifying the registry, and more.
          </p>
          <p>The agent will always show a preview of the action and ask for your confirmation before applying it.</p>
          <p>By using Agent you agree to supervise every change and acknowledge the AI may make mistakes.</p>
        </div>
      </Modal>
      <VideoModal
        isOpen={videoModal.isOpen}
        title={videoModal.title}
        videoUrl={videoModal.url}
        onClose={handleVideoClose}
      />
      <Modal
        isOpen={showMultiLotWarning}
        title="Multiple Lots Selected"
        description={`AI Chat only supports one lot at a time. Continuing will unselect all but "${getPrimaryLot()?.lotName || 'the primary lot'}".`}
        confirmText="Continue"
        cancelText="Cancel"
        onConfirm={confirmMultiLotWarning}
        onCancel={() => setShowMultiLotWarning(false)}
      />
    </>
  );
};

export default Chat;
